(() => {
  const ELEMENT_NAME = 'valthea-diamond-tray';
  const RETURN_KEY = 'valthea:return:v1';
  const RETURN_TTL_MS = 4 * 60 * 60 * 1000;
  const POPUP_EXCLUDED_PATH = '/pages/valthea-test-page';
  const EXCLUDED_POPUP_SELECTOR =
    'popup-block.premium-newsletter-popup[data-type="premium-newsletter"][data-block-id="PremiumNewsletterTyhqma"]';

  const normalizedPath = (path) => {
    const value = String(path || '').replace(/\/+$/, '');
    return value || '/';
  };

  const suppressExcludedPagePopup = () => {
    if (normalizedPath(window.location.pathname) !== POPUP_EXCLUDED_PATH) return;

    document.querySelectorAll(EXCLUDED_POPUP_SELECTOR).forEach((popup) => {
      const popupWasScheduledHere =
        Object.prototype.hasOwnProperty.call(popup, 'timer') && popup.timer !== undefined;

      if (popupWasScheduledHere) {
        window.clearTimeout(popup.timer);

        // Maker records the seven-day cooldown as soon as it schedules the
        // popup. Remove only the record created by this page so the same popup
        // can still appear normally on every other storefront page.
        if (popup.id) {
          try {
            window.localStorage.removeItem(popup.id);
          } catch (_error) {
            // Local storage can be unavailable in strict privacy modes.
          }
        }
      }

      if (popup.observer && typeof popup.observer.disconnect === 'function') {
        popup.observer.disconnect();
      }

      const popupWasOpen =
        popup.getAttribute('aria-hidden') === 'false' || popup.hasAttribute('data-transition-active');

      if (popupWasOpen && typeof popup.close === 'function') {
        try {
          popup.close();
        } catch (_error) {
          // Hiding and removing the element below remains a safe fallback.
        }
      }

      const popupClass = popup.constructor;
      if (popupClass && Array.isArray(popupClass.instances)) {
        const instanceIndex = popupClass.instances.indexOf(popup);
        if (instanceIndex >= 0) popupClass.instances.splice(instanceIndex, 1);
      }

      popup.setAttribute('aria-hidden', 'true');
      popup.removeAttribute('data-transition-active');
      popup.hidden = true;
      popup.remove();

      if (popupClass && typeof popupClass.loadNext === 'function') {
        popupClass.loadNext();
      }
    });
  };

  suppressExcludedPagePopup();
  if (!window.__valtheaPopupExclusionBound) {
    window.__valtheaPopupExclusionBound = true;
    window.addEventListener('pageshow', suppressExcludedPagePopup, { passive: true });
    document.addEventListener('shopify:section:load', suppressExcludedPagePopup);
  }

  if (customElements.get(ELEMENT_NAME)) return;

  const storageGet = (key) => {
    try {
      return window.sessionStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  };

  const storageSet = (key, value) => {
    try {
      window.sessionStorage.setItem(key, value);
      return true;
    } catch (_error) {
      return false;
    }
  };

  const storageRemove = (key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch (_error) {
      // Storage can be unavailable in strict privacy modes. The UI still works.
    }
  };

  const clamp = (value, minimum = 0, maximum = 1) => (
    Math.min(maximum, Math.max(minimum, Number(value) || 0))
  );

  const smoothstep = (start, end, value) => {
    if (end <= start) return value >= end ? 1 : 0;
    const progress = clamp((value - start) / (end - start));
    return progress * progress * (3 - 2 * progress);
  };

  const smootherstep = (value) => {
    const progress = clamp(value);
    return progress * progress * progress * (progress * (progress * 6 - 15) + 10);
  };

  // Mobile storytelling is divided into deliberate chapters. One gesture can
  // enter only one chapter, so a high-velocity swipe cannot skip the door,
  // walk-in, tray transition, or the staged diamond/nameplate reveal.
  const MOBILE_STORY_STEPS = Object.freeze([
    { progress: 0, duration: 0 },
    { progress: 0.17, duration: 700 },
    { progress: 0.26, duration: 780 },
    { progress: 0.64, duration: 2200 }
  ]);
  const MOBILE_STORY_AXIS_THRESHOLD = 10;
  const MOBILE_STORY_SWIPE_THRESHOLD = 22;
  const MOBILE_STORY_GATE_PROGRESS = 0.64;
  const MOBILE_STORY_REVEAL_PROGRESS = 0.91;
  const MOBILE_STORY_COMPLETE_PROGRESS = 0.905;
  const MOBILE_STORY_WHEEL_IDLE_MS = 220;
  const STORY_FRAME_TIMEOUT_MS = 6000;
  const STORY_FRAME_PAINT_FALLBACK_MS = 450;

  const parseReturnContext = () => {
    const raw = storageGet(RETURN_KEY);
    if (!raw) return null;

    try {
      const context = JSON.parse(raw);
      let trayUrl = null;
      let destinationUrl = null;

      try {
        trayUrl = typeof context.trayPath === 'string'
          ? new URL(context.trayPath, window.location.origin)
          : null;
        destinationUrl = typeof context.destinationPath === 'string'
          ? new URL(context.destinationPath, window.location.origin)
          : null;
      } catch (_error) {
        trayUrl = null;
        destinationUrl = null;
      }

      const isValid =
        context &&
        context.v === 1 &&
        context.origin === window.location.origin &&
        typeof context.trayPath === 'string' &&
        context.trayPath.startsWith('/') &&
        !context.trayPath.startsWith('//') &&
        typeof context.destinationPath === 'string' &&
        context.destinationPath.startsWith('/') &&
        !context.destinationPath.startsWith('//') &&
        trayUrl &&
        trayUrl.origin === window.location.origin &&
        destinationUrl &&
        destinationUrl.origin === window.location.origin &&
        typeof context.batch === 'string' &&
        Number.isFinite(context.createdAt) &&
        context.createdAt <= Date.now() + 60 * 1000 &&
        Date.now() - context.createdAt <= RETURN_TTL_MS;

      if (!isValid) {
        storageRemove(RETURN_KEY);
        return null;
      }

      return context;
    } catch (_error) {
      storageRemove(RETURN_KEY);
      return null;
    }
  };

  class ValtheaDiamondTray extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abortController = new AbortController();
      this.sectionId = this.dataset.sectionId || '';
      this.designMode = this.dataset.designMode === 'true' || Boolean(window.Shopify && window.Shopify.designMode);
      this.mobileBreakpoint = Number(this.dataset.mobileBreakpoint) || 750;
      this.introMode = this.dataset.introMode || 'session';
      this.introVersion = this.dataset.introVersion || '1';
      const parsedTransitionMs = Number(this.dataset.transitionMs);
      this.transitionMs = Number.isFinite(parsedTransitionMs) ? Math.max(0, parsedTransitionMs) : 500;
      this.fallbackSeconds = Math.max(5, Number(this.dataset.fallbackSeconds) || 18);
      this.returnLabel = this.dataset.returnLabel || '返回托盘';
      this.selectionHint = this.dataset.selectionHint || '再次点击查看详情';
      this.zoomReturnHint = this.dataset.zoomReturnHint || '点击空白处返回托盘';
      this.introLabel = this.dataset.introLabel || 'Valthea 开场视频';
      let configuredDoorTime = Number(this.dataset.storyDoorTime);
      let configuredTrayTime = Number(this.dataset.storyTrayTime);
      let configuredRevealMs = Number(this.dataset.storyRevealMs);
      if (!Number.isFinite(configuredDoorTime)) configuredDoorTime = 40;
      if (!Number.isFinite(configuredTrayTime)) configuredTrayTime = 72;
      if (!Number.isFinite(configuredRevealMs)) configuredRevealMs = 3600;

      // Transparently migrate the first story release's defaults. This keeps
      // existing merchant data untouched while moving the pause to the last
      // fully closed-door frame and allowing the filmed tray to settle first.
      if (configuredDoorTime === 42) configuredDoorTime = 40;
      if (configuredTrayTime === 64) configuredTrayTime = 72;
      if (configuredRevealMs === 2400) configuredRevealMs = 3600;

      this.storyDoorTime = Math.max(0, configuredDoorTime) / 10;
      this.storyTrayTime = Math.max(this.storyDoorTime + 0.4, configuredTrayTime / 10);
      this.storyLogoMs = Math.max(300, Number(this.dataset.storyLogoMs) || 1600);
      this.storyRevealMs = Math.max(600, configuredRevealMs);
      this.storyTrayStartScale = clamp((Number(this.dataset.storyTrayStartScale) || 86) / 100, 0.8, 0.96);

      this.storyPin = this.querySelector('[data-vdt-story-pin]');
      this.intro = this.querySelector('[data-vdt-intro]');
      this.videoHost = this.querySelector('[data-vdt-video-host]');
      this.brandIntro = this.querySelector('[data-vdt-brand]');
      this.brandLight = this.querySelector('.vdt__brand-light');
      this.doorTrigger = this.querySelector('[data-vdt-door]');
      this.trayTrigger = this.querySelector('[data-vdt-tray-trigger]');
      this.scrollCue = this.querySelector('[data-vdt-scroll-cue]');
      this.storyProgress = this.querySelector('.vdt__story-progress');
      this.stage = this.querySelector('[data-vdt-stage]');
      this.trayLayout = this.querySelector('.vdt__tray-layout');
      this.canvas = this.querySelector('[data-vdt-canvas]');
      this.storyControls = this.querySelector('[data-vdt-controls]');
      this.batches = Array.from(this.querySelectorAll('[data-vdt-batch]'));
      this.nextButton = this.querySelector('[data-vdt-next]');
      this.pageIndicator = this.querySelector('[data-vdt-page-indicator]');
      this.selectionTip = this.querySelector('[data-vdt-selection-tip]');
      this.status = this.querySelector('[data-vdt-status]');
      this.editorWarning = this.querySelector('[data-vdt-editor-warning]');
      this.activeIndex = 0;
      this.selectedDiamond = null;
      this.zoomView = null;
      this.zoomMeteorAnimations = [];
      this.video = null;
      this.watchdogTimer = null;
      this.introCleanupTimer = null;
      this.introPlaybackCleanup = null;
      this.introPlayPending = false;
      this.storyMode = '';
      this.storyInteractive = false;
      this.storyMetadataReady = false;
      this.storyFirstFrameReady = false;
      this.storyFirstFrameCleanup = null;
      this.storyDesktopStarted = false;
      this.storyDesktopAwaitingTray = false;
      this.storyDesktopRevealing = false;
      this.storyDoorHadFocus = false;
      this.storyLogoFrame = null;
      this.storyPlaybackFrame = null;
      this.storyRevealFrame = null;
      this.storyScrollFrame = null;
      this.storyMobileStepFrame = null;
      this.storyMobileSyncFrame = null;
      this.storyMobileRevealSettleFrame = null;
      this.storyLogoTimer = null;
      this.storySeekTimer = null;
      this.storyWalkingTimer = null;
      this.storySeekCleanup = null;
      this.storyMobileSeekCleanup = null;
      this.storyMobileSeekTimer = null;
      this.storyScrollHandler = null;
      this.storyDesiredTime = this.storyDoorTime;
      this.storyVideoPrimed = false;
      this.storyMobileTouchIdentifier = null;
      this.storyMobileTouchStartX = 0;
      this.storyMobileTouchStartY = 0;
      this.storyMobileTouchAxis = '';
      this.storyMobileTouchCommitted = false;
      this.storyMobileTouchBlocked = false;
      this.storyMobileAwaitingTray = false;
      this.storyMobileTrayRevealStarted = false;
      this.storyMobileTrayRevealComplete = false;
      this.storyMobileWalkingPlayback = false;
      this.storyMobilePlaybackToken = 0;
      this.storyMobileRenderedProgress = 0;
      this.storyMobileAppliedRevealProgress = null;
      this.storyMobileInternalScroll = false;
      this.storyMobileWheelGestureActive = false;
      this.storyMobileWheelTimer = null;
      this.storyMobileGateTimer = null;
      this.storyMobileGateCleanup = null;
      this.storyMobileWaitingForFirstFrame = false;
      this.storyMobileDeferredStep = null;
      this.storyMobileChapterIndex = 0;
      this.storyMobileTargetChapterIndex = null;
      this.storyMobilePendingDirections = [];
      this.storyMobileViewportHeight = 0;
      this.storyMobileVisualViewportOffset = 0;
      this.storyMobileVisualViewportFrame = null;
      this.storyMobileVisualViewportTimer = null;
      this.storyMobileScrollBehaviorRestore = null;
      this.storyTrayAssetPromise = null;
      this.storyRevealCache = null;
      this.hitboxSyncFrame = null;
      this.hitboxResizeObserver = null;

      this.normalizeDomState();
      this.bindEvents();
      this.validateEditorConfiguration();

      const restoredContext = this.consumeReturnContextForThisTray();
      const restoredBatch = restoredContext ? restoredContext.batch : null;

      if (restoredBatch) {
        this.showBatchByNumber(restoredBatch, false);
      } else {
        this.showBatch(0, false);
      }

      this.updateBatchControls();
      this.setupIntro(Boolean(restoredContext));
      this.observeDiamondHitboxes();
      this.scheduleDiamondHitboxSync();

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          if (this.isConnected && this.initialized) this.scheduleDiamondHitboxSync();
        });
      }
    }

    disconnectedCallback() {
      this.initialized = false;
      if (this.abortController) this.abortController.abort();
      if (this.hitboxSyncFrame) window.cancelAnimationFrame(this.hitboxSyncFrame);
      this.hitboxSyncFrame = null;
      if (this.hitboxResizeObserver) this.hitboxResizeObserver.disconnect();
      this.hitboxResizeObserver = null;
      this.clearTimers();
      this.unloadVideo();
      this.removeZoom();
    }

    normalizeDomState() {
      this.classList.remove(
        'has-selection',
        'is-navigating',
        'is-story-active',
        'is-story-desktop',
        'is-story-mobile',
        'is-story-brand',
        'is-story-door',
        'is-story-walking',
        'is-story-tray-gate',
        'is-story-revealing',
        'is-story-mobile-stepping',
        'is-story-mobile-tray-gate',
        'is-story-mobile-revealing',
        'is-story-stage-visible',
        'is-story-interactive'
      );
      this.classList.toggle('is-tray-visible', this.classList.contains('vdt--no-video'));

      this.querySelectorAll('[data-vdt-diamond]').forEach((diamond) => {
        diamond.classList.remove('is-selected');
        diamond.setAttribute('aria-pressed', 'false');
      });

      if (this.selectionTip) this.selectionTip.hidden = true;
      if (this.doorTrigger) this.doorTrigger.hidden = true;
      if (this.trayTrigger) this.trayTrigger.hidden = true;
      this.style.removeProperty('--vdt-story-tray-scale');
      if (this.intro) {
        this.intro.hidden = false;
        this.intro.setAttribute('aria-hidden', this.classList.contains('vdt--no-video') ? 'true' : 'false');
        this.intro.removeAttribute('inert');
      }
      if (this.stage) {
        const trayIsImmediatelyVisible = this.classList.contains('vdt--no-video');
        this.stage.setAttribute('aria-hidden', trayIsImmediatelyVisible ? 'false' : 'true');
        if (trayIsImmediatelyVisible) {
          this.stage.removeAttribute('inert');
        } else {
          this.stage.setAttribute('inert', '');
        }
      }
      if (this.editorWarning) {
        this.editorWarning.hidden = true;
        this.editorWarning.textContent = '';
      }
    }

    bindEvents() {
      const signal = this.abortController.signal;

      this.addEventListener('click', (event) => this.handleClick(event), { signal });
      document.addEventListener('click', (event) => this.handleDocumentClick(event), { signal });
      document.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
      document.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event), { signal });
      window.addEventListener('pageshow', () => this.handlePageShow(), { signal });
      window.addEventListener('resize', () => {
        if (this.zoomView && this.selectedDiamond) {
          this.applyZoomPresentation(this.selectedDiamond, this.zoomView);
        }
        this.scheduleDiamondHitboxSync();
        this.updateZoomMeteor();
        if (this.storyMode === 'desktop' && this.isMobileStoryViewport()) {
          this.revealTray();
          return;
        }
        if (this.hasMobileStoryLayout()) {
          if (this.isMobileStoryViewport()) {
            this.scheduleMobileVisualViewportSync();
          } else {
            this.revealTray();
          }
        }
      }, { signal });

      if (window.visualViewport) {
        const syncVisualViewport = () => {
          if (this.hasMobileStoryLayout() && this.isMobileStoryViewport()) {
            this.scheduleMobileVisualViewportSync();
          }
        };
        window.visualViewport.addEventListener('resize', syncVisualViewport, { passive: true, signal });
        window.visualViewport.addEventListener('scroll', syncVisualViewport, { passive: true, signal });
      }
    }

    scheduleDiamondHitboxSync() {
      if (!this.isConnected) return;
      // Hitbox measurement reads and writes every diamond/nameplate rectangle.
      // The story is not clickable yet, so defer this expensive work until the
      // tray becomes interactive instead of doing it during mobile swipes.
      if (this.storyMode === 'mobile' && !this.storyInteractive) return;
      if (this.hitboxSyncFrame) window.cancelAnimationFrame(this.hitboxSyncFrame);
      this.hitboxSyncFrame = window.requestAnimationFrame(() => {
        this.hitboxSyncFrame = null;
        if (!this.isConnected) return;
        this.syncDiamondHitboxes();
      });
    }

    observeDiamondHitboxes() {
      if (typeof ResizeObserver !== 'function') return;
      if (this.hitboxResizeObserver) this.hitboxResizeObserver.disconnect();

      this.hitboxResizeObserver = new ResizeObserver(() => this.scheduleDiamondHitboxSync());
      if (this.canvas) this.hitboxResizeObserver.observe(this.canvas);
      this.querySelectorAll('.vdt__diamond-visual, .vdt__nameplate').forEach((element) => {
        this.hitboxResizeObserver.observe(element);
      });
    }

    syncDiamondHitboxes() {
      const diamonds = Array.from(this.querySelectorAll('[data-vdt-diamond]'));

      // Always measure from the CSS fallback geometry. This lets a hitbox shrink
      // again after a viewport or editor-setting change instead of retaining a
      // previously resolved size.
      diamonds.forEach((diamond) => {
        diamond.style.removeProperty('--vdt-hit-width-resolved');
        diamond.style.removeProperty('--vdt-hit-up-resolved');
        diamond.style.removeProperty('--vdt-hit-shift-x-resolved');
        diamond.style.removeProperty('--vdt-hit-height-resolved');
      });

      const canvasWidth = this.canvas ? this.canvas.getBoundingClientRect().width : 0;
      const interRowGap = Math.max(4, Math.min(8, canvasWidth * 0.01));
      const nameplateObstacles = diamonds.map((diamond) => {
        const nameplate = diamond.querySelector('.vdt__nameplate');
        if (!nameplate) return null;

        const rect = nameplate.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return null;

        return {
          diamond,
          batch: diamond.closest('[data-vdt-batch]'),
          rect
        };
      }).filter(Boolean);

      const measurements = diamonds.map((diamond) => {
        const visual = diamond.querySelector('.vdt__diamond-visual');
        const nameplate = diamond.querySelector('.vdt__nameplate');
        if (!visual) return null;

        const diamondRect = diamond.getBoundingClientRect();
        const visualRect = visual.getBoundingClientRect();
        const nameplateRect = nameplate ? nameplate.getBoundingClientRect() : null;
        const layoutWidth = diamond.offsetWidth;
        const layoutHeight = diamond.offsetHeight;
        const scaleX = layoutWidth > 0 ? diamondRect.width / layoutWidth : 1;
        const scaleY = layoutHeight > 0 ? diamondRect.height / layoutHeight : 1;
        const visualTop = Number.parseFloat(window.getComputedStyle(visual).top);

        if (
          !Number.isFinite(scaleX) || scaleX <= 0 ||
          !Number.isFinite(scaleY) || scaleY <= 0 ||
          !Number.isFinite(visualTop) ||
          visualRect.width <= 0 || visualRect.height <= 0
        ) {
          return null;
        }

        const anchorX = diamondRect.left + diamondRect.width / 2;
        const anchorY = diamondRect.top + visualTop * scaleY;
        const edgePadding = 2;
        const contentRects = [visualRect];

        if (nameplateRect && nameplateRect.width > 0 && nameplateRect.height > 0) {
          contentRects.push(nameplateRect);
        }

        const contentLeft = Math.min(...contentRects.map((rect) => rect.left)) - edgePadding;
        const contentRight = Math.max(...contentRects.map((rect) => rect.right)) + edgePadding;
        const contentTop = Math.min(...contentRects.map((rect) => rect.top)) - edgePadding;
        const contentBottom = Math.max(...contentRects.map((rect) => rect.bottom));

        // Keep the annotated row-specific minimum box, then expand only the
        // side(s) needed by this diamond and its independently positioned plate.
        const resolvedLeft = Math.min(diamondRect.left, contentLeft);
        const resolvedRight = Math.max(diamondRect.right, contentRight);
        const rawResolvedTop = Math.min(diamondRect.top, contentTop);
        const currentBatch = diamond.closest('[data-vdt-batch]');
        const blockingBottoms = nameplateObstacles
          .filter((obstacle) => (
            obstacle.diamond !== diamond &&
            obstacle.batch === currentBatch &&
            obstacle.rect.bottom < anchorY &&
            obstacle.rect.right > resolvedLeft &&
            obstacle.rect.left < resolvedRight
          ))
          .map((obstacle) => obstacle.rect.bottom);
        const requestedTop = blockingBottoms.length
          ? Math.max(...blockingBottoms) + interRowGap
          : rawResolvedTop;
        const maximumTop = Math.min(
          anchorY - scaleY,
          contentBottom - scaleY
        );
        const resolvedTop = Math.max(
          rawResolvedTop,
          Math.min(requestedTop, maximumTop)
        );
        const resolvedWidth = (resolvedRight - resolvedLeft) / scaleX;
        const resolvedShiftX = (
          (resolvedLeft + resolvedRight) / 2 - anchorX
        ) / scaleX;
        const resolvedUp = (anchorY - resolvedTop) / scaleY;
        const resolvedHeight = (contentBottom - resolvedTop) / scaleY;

        if (
          !Number.isFinite(resolvedWidth) || resolvedWidth <= 0 ||
          !Number.isFinite(resolvedShiftX) ||
          !Number.isFinite(resolvedUp) || resolvedUp < 0 ||
          !Number.isFinite(resolvedHeight) || resolvedHeight <= 0
        ) {
          return null;
        }

        return {
          diamond,
          resolvedWidth,
          resolvedShiftX,
          resolvedUp,
          resolvedHeight
        };
      }).filter(Boolean);

      // Apply after every rectangle has been read so one write cannot affect a
      // later measurement in the same frame.
      measurements.forEach((measurement) => {
        measurement.diamond.style.setProperty(
          '--vdt-hit-width-resolved',
          `${measurement.resolvedWidth.toFixed(3)}px`
        );
        measurement.diamond.style.setProperty(
          '--vdt-hit-shift-x-resolved',
          `${measurement.resolvedShiftX.toFixed(3)}px`
        );
        measurement.diamond.style.setProperty(
          '--vdt-hit-up-resolved',
          `${measurement.resolvedUp.toFixed(3)}px`
        );
        measurement.diamond.style.setProperty(
          '--vdt-hit-height-resolved',
          `${measurement.resolvedHeight.toFixed(3)}px`
        );
      });
    }

    handleClick(event) {
      const doorTrigger = event.target.closest('[data-vdt-door]');
      if (doorTrigger && this.contains(doorTrigger)) {
        event.preventDefault();
        this.enterDesktopStory();
        return;
      }

      const trayTrigger = event.target.closest('[data-vdt-tray-trigger]');
      if (trayTrigger && this.contains(trayTrigger)) {
        event.preventDefault();
        if (this.storyMode === 'mobile') {
          this.startMobileTrayReveal();
        } else {
          this.startDesktopTrayReveal();
        }
        return;
      }

      const skipButton = event.target.closest('[data-vdt-skip]');
      if (skipButton && this.contains(skipButton)) {
        event.preventDefault();
        this.markIntroSeen();
        this.revealTray({ focusFirstDiamond: true });
        return;
      }

      const soundButton = event.target.closest('[data-vdt-sound]');
      if (soundButton && this.contains(soundButton)) {
        event.preventDefault();
        this.toggleSound(soundButton);
        return;
      }

      const diamond = event.target.closest('[data-vdt-diamond]');
      if (diamond && this.contains(diamond)) {
        event.preventDefault();
        event.stopPropagation();
        this.activateDiamond(diamond);
        return;
      }

      const nextButton = event.target.closest('[data-vdt-next]');
      if (nextButton && this.contains(nextButton)) {
        event.preventDefault();
        this.nextBatch();
        return;
      }

      const trackedLink = event.target.closest('[data-vdt-link]');
      if (trackedLink && this.contains(trackedLink)) {
        const modifiedClick =
          event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

        if (modifiedClick) {
          const preparedUrl = this.prepareNavigation(trackedLink.getAttribute('href'));
          if (preparedUrl) trackedLink.href = preparedUrl.href;
          return;
        }

        event.preventDefault();
        this.navigate(trackedLink.getAttribute('href'));
        return;
      }

      if (event.target.closest('[data-vdt-controls]')) return;

      if (event.target.closest('[data-vdt-canvas]')) {
        this.clearSelection(true);
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.selectedDiamond) {
        event.preventDefault();
        this.clearSelection(true, true);
      }
    }

    handleDocumentClick(event) {
      if (!this.selectedDiamond || !(event.target instanceof Element)) return;
      if (this.zoomView && this.zoomView.contains(event.target)) return;
      if (this.contains(event.target) && event.target.closest('[data-vdt-diamond]')) return;
      this.clearSelection(false);
    }

    handleBlockSelect(event) {
      if (!this.designMode) return;
      if (event.detail && event.detail.sectionId && String(event.detail.sectionId) !== String(this.sectionId)) return;

      const blockId = event.detail && event.detail.blockId ? String(event.detail.blockId) : '';
      if (!blockId) return;

      const diamond = Array.from(this.querySelectorAll('[data-vdt-diamond]')).find(
        (item) => item.dataset.blockId === blockId
      );

      if (!diamond) return;
      this.revealTray();
      this.showBatchByNumber(diamond.dataset.batch, false);
      this.selectDiamond(diamond, false);
    }

    handlePageShow() {
      const restoredContext = this.consumeReturnContextForThisTray();
      if (restoredContext) {
        this.showBatchByNumber(restoredContext.batch, false);
        this.clearSelection(false);
        this.revealTray();
        return;
      }

      if (window.location.hash === '#valthea-tray') {
        this.clearSelection(false);
        this.revealTray();
      }
    }

    setupIntro(restoredFromDestination) {
      const desktopTemplate = this.querySelector('template[data-vdt-video-template="desktop"]');
      const mobileTemplate = this.querySelector('template[data-vdt-video-template="mobile"]');
      const hasVideo = Boolean(desktopTemplate || mobileTemplate);
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const skipForReducedMotion = this.dataset.skipReducedMotion === 'true';
      const previewInEditor = this.dataset.previewIntroEditor === 'true';
      const alreadySeen = storageGet(this.introStorageKey()) === '1';
      const returnedToTray = restoredFromDestination || window.location.hash === '#valthea-tray';

      const shouldSkip =
        !hasVideo ||
        this.introMode === 'never' ||
        returnedToTray ||
        (this.designMode && !previewInEditor) ||
        (skipForReducedMotion && reducedMotion) ||
        (this.introMode === 'session' && alreadySeen);

      if (shouldSkip) {
        this.revealTray();
        return;
      }

      this.startIntro();
    }

    startIntro() {
      if (this.dataset.nativeStory === 'true') {
        this.startNativeStory();
        return;
      }
      // The new experience intentionally uses the portrait film on every
      // viewport. Desktop frames it like a cinematic doorway; mobile scrubs it
      // with gestures. Mobile prefers a decode-friendly source; desktop keeps
      // the full-resolution portrait source so image quality is not reduced.
      const mobileViewport = this.isMobileStoryViewport();
      const template = mobileViewport
        ? this.querySelector('template[data-vdt-video-template="mobile"]') ||
          this.querySelector('template[data-vdt-video-template="portrait-desktop"]') ||
          this.querySelector('template[data-vdt-video-template="desktop"]')
        : this.querySelector('template[data-vdt-video-template="portrait-desktop"]') ||
          this.querySelector('template[data-vdt-video-template="mobile"]') ||
          this.querySelector('template[data-vdt-video-template="desktop"]');

      if (!template || !this.videoHost) {
        this.revealTray();
        return;
      }

      const video = document.createElement('video');
      video.autoplay = false;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('x5-playsinline', '');
      video.setAttribute('x5-video-player-type', 'h5-page');
      video.setAttribute('muted', '');
      video.setAttribute('aria-label', this.introLabel);

      if (template.dataset.poster) video.poster = template.dataset.poster;
      video.appendChild(template.content.cloneNode(true));
      this.videoHost.replaceChildren(video);
      this.video = video;
      this.storyFirstFrameReady = false;
      this.clearStoryFirstFrameGate();

      this.classList.add('is-story-active');
      this.classList.remove('is-tray-visible');
      if (this.intro) {
        this.intro.hidden = false;
        this.intro.setAttribute('aria-hidden', 'false');
      }
      this.setStoryInteractive(false);

      video.addEventListener('loadedmetadata', () => {
        if (video !== this.video) return;
        this.storyMetadataReady = true;
        if (this.storyMode === 'desktop') {
          this.prepareDesktopDoor();
        } else if (this.storyMode === 'mobile') {
          this.scheduleMobileStoryUpdate();
        }
      });
      video.addEventListener('loadeddata', () => {
        this.queueStoryFirstFrameReady(video);
        if (this.storyMode === 'mobile') this.scheduleMobileStoryUpdate();
      }, { passive: true });
      video.addEventListener('canplay', () => this.queueStoryFirstFrameReady(video), {
        passive: true
      });
      video.addEventListener('seeked', () => this.queueStoryFirstFrameReady(video), {
        passive: true
      });
      video.addEventListener('playing', () => this.queueStoryFirstFrameReady(video), {
        passive: true
      });
      video.addEventListener('error', () => this.revealTray(), { once: true });

      this.scheduleWatchdog(video);
      video.load();

      if (this.isMobileStoryViewport()) {
        this.setupMobileStory();
      } else {
        this.setupDesktopStory();
      }
    }

    startNativeStory() {
      const signal = this.abortController.signal;
      this.classList.add('vdt--native');
      this.classList.remove('is-tray-visible');
      const scene = document.createElement('div');
      scene.className = 'vdt-native-scene';
      const room = document.createElement('img');
      room.className = 'vdt-native-room'; room.src = this.dataset.roomImage; room.alt = '';
      const doors = document.createElement('div'); doors.className = 'vdt-native-doors';
      doors.innerHTML = '<div></div><div></div>';
      const brand = document.createElement('div'); brand.className = 'vdt-native-brand';
      brand.textContent = this.brandIntro?.textContent.trim() || 'VALTHEA';
      const enter = document.createElement('button'); enter.type = 'button'; enter.className = 'vdt-native-enter';
      const mobile = this.isMobileStoryViewport();
      enter.textContent = mobile ? 'Scroll to enter · or tap' : 'Enter the atelier';
      const reveal = document.createElement('button'); reveal.type = 'button'; reveal.className = 'vdt-native-reveal';
      reveal.textContent = 'Discover the diamonds'; reveal.hidden = true;
      scene.append(room, doors, brand, enter, reveal);
      this.intro.replaceChildren(scene);
      this.intro.hidden = false;
      let active = true, frame = 0, timed = false;
      const render = progress => {
        const door = smoothstep(0.1, 0.5, progress);
        doors.children[0].style.transform = `perspective(900px) rotateY(${-95 * door}deg)`;
        doors.children[1].style.transform = `perspective(900px) rotateY(${95 * door}deg)`;
        doors.style.opacity = String(1 - smoothstep(.5,.7,progress));
        brand.style.opacity = String(1 - smoothstep(0,.16,progress));
        room.style.transform = `scale(${1 + .3 * smoothstep(.45,1,progress)})`;
        enter.hidden = progress > .08;
        reveal.hidden = progress < .98;
        scene.classList.toggle('is-ready', progress >= .98);
      };
      const animate = () => {
        if (!active || timed) return;
        timed = true; const started = performance.now();
        const tick = now => {if(!active)return;const p=clamp((now-started)/3600);render(p);if(p<1)frame=requestAnimationFrame(tick);};
        frame=requestAnimationFrame(tick);
      };
      enter.addEventListener('click',animate,{signal});
      const cleanup = () => {active=false;cancelAnimationFrame(frame);window.removeEventListener('scroll',scroll);this.classList.remove('vdt--native-scrolling');};
      const finish = () => {
        cleanup(); this.revealTray({focusFirstDiamond:true});
        this.stage.animate([{transform:'translateY(100%)',opacity:0},{transform:'translateY(0)',opacity:1}],{duration:900,easing:'cubic-bezier(.2,.7,.2,1)'});
        this.scrollIntoView({block:'start'});
      };
      reveal.addEventListener('click',finish,{signal});
      const skip = document.createElement('button'); skip.type='button';skip.className='vdt-native-skip';skip.textContent='Skip intro';skip.addEventListener('click',finish,{signal});scene.append(skip);
      const scroll = () => {if(!active||timed||frame)return;frame=requestAnimationFrame(()=>{frame=0;const distance=Math.max(1,this.offsetHeight-window.innerHeight);render(clamp(-this.getBoundingClientRect().top/distance));});};
      if(mobile){this.classList.add('vdt--native-scrolling');window.addEventListener('scroll',scroll,{passive:true,signal});}
      this.nativeStoryCleanup = cleanup;
      signal.addEventListener('abort',cleanup,{once:true});
      render(0);
    }

    queueStoryFirstFrameReady(video) {
      if (
        !video ||
        video !== this.video ||
        video.readyState < 2 ||
        this.storyFirstFrameReady ||
        this.storyFirstFrameCleanup
      ) return;

      let completed = false;
      let frameCallbackId = null;
      let paintTimer = null;
      let firstPaintFrame = null;
      let secondPaintFrame = null;

      const releaseWaiters = () => {
        if (
          frameCallbackId !== null &&
          typeof video.cancelVideoFrameCallback === 'function'
        ) {
          try {
            video.cancelVideoFrameCallback(frameCallbackId);
          } catch (_error) {
            // A frame callback that is already running no longer needs cancelling.
          }
        }
        frameCallbackId = null;
        if (paintTimer) window.clearTimeout(paintTimer);
        paintTimer = null;
        if (firstPaintFrame) window.cancelAnimationFrame(firstPaintFrame);
        firstPaintFrame = null;
        if (secondPaintFrame) window.cancelAnimationFrame(secondPaintFrame);
        secondPaintFrame = null;
      };

      const finish = (presentedTime) => {
        if (completed) return;
        completed = true;
        releaseWaiters();
        this.storyFirstFrameCleanup = null;
        if (video !== this.video || video.readyState < 2 || !this.isConnected) return;

        if (this.storyMode === 'mobile') {
          const expectedTime = Number(this.storyDesiredTime);
          const visibleTime = Number.isFinite(presentedTime)
            ? Number(presentedTime)
            : Number(video.currentTime);
          if (
            video.seeking ||
            (
              Number.isFinite(expectedTime) &&
              Number.isFinite(visibleTime) &&
              Math.abs(visibleTime - expectedTime) >= 0.18
            )
          ) {
            this.setStoryVideoTime(expectedTime);
            return;
          }
        }

        this.storyFirstFrameReady = true;
        this.clearWatchdog();
        if (this.storyMode === 'mobile') {
          this.scheduleMobileStoryUpdate();
          this.resumeDeferredMobileStoryStep();
        }
      };

      this.storyFirstFrameCleanup = () => {
        if (completed) return;
        completed = true;
        releaseWaiters();
        this.storyFirstFrameCleanup = null;
      };

      if (typeof video.requestVideoFrameCallback === 'function') {
        try {
          frameCallbackId = video.requestVideoFrameCallback((_now, metadata) => {
            frameCallbackId = null;
            finish(metadata && metadata.mediaTime);
          });
          // Paused video frames are not reported consistently by older WebViews.
          // loadeddata still guarantees a decodable current frame, so use it as
          // a bounded paint fallback instead of leaving the story black forever.
          paintTimer = window.setTimeout(finish, STORY_FRAME_PAINT_FALLBACK_MS);
          return;
        } catch (_error) {
          frameCallbackId = null;
        }
      }

      firstPaintFrame = window.requestAnimationFrame(() => {
        firstPaintFrame = null;
        secondPaintFrame = window.requestAnimationFrame(() => {
          secondPaintFrame = null;
          finish();
        });
      });
    }

    clearStoryFirstFrameGate() {
      if (this.storyFirstFrameCleanup) this.storyFirstFrameCleanup();
      this.storyFirstFrameCleanup = null;
      this.storyMobileWaitingForFirstFrame = false;
      this.storyMobileDeferredStep = null;
    }

    resumeDeferredMobileStoryStep() {
      if (
        this.storyMode !== 'mobile' ||
        !this.storyFirstFrameReady ||
        !this.storyMobileWaitingForFirstFrame ||
        !this.storyMobileDeferredStep
      ) return;

      const deferredStep = this.storyMobileDeferredStep;
      this.storyMobileDeferredStep = null;
      this.storyMobileWaitingForFirstFrame = false;
      this.storyMobileTargetChapterIndex = null;
      if (!this.startMobileStoryStep(deferredStep.direction, deferredStep.options)) {
        this.drainMobileStoryStepQueue();
      }
    }

    isMobileStoryViewport() {
      return window.matchMedia(
        `(max-width: ${this.mobileBreakpoint}px) and (orientation: portrait)`
      ).matches;
    }

    hasMobileStoryLayout() {
      return (
        this.classList.contains('is-story-mobile') &&
        (this.storyMode === 'mobile' || this.storyMode === 'complete')
      );
    }

    setupDesktopStory() {
      this.storyMode = 'desktop';
      this.storyLogoElapsed = false;
      this.storyDesktopStarted = false;
      this.storyDesktopAwaitingTray = false;
      this.storyDesktopRevealing = false;
      this.classList.add('is-story-desktop', 'is-story-brand');
      this.classList.remove('is-story-mobile', 'is-story-door', 'is-story-walking', 'is-story-tray-gate');
      if (this.doorTrigger) this.doorTrigger.hidden = true;
      if (this.trayTrigger) this.trayTrigger.hidden = true;

      this.style.setProperty('--vdt-story-intro-opacity', '1');
      this.style.setProperty('--vdt-story-video-opacity', '0');
      this.style.setProperty('--vdt-story-video-scale', '1');
      this.style.setProperty('--vdt-story-brand-opacity', '1');
      this.style.setProperty('--vdt-story-brand-progress', '0');
      this.style.setProperty('--vdt-story-brand-scale', '0.965');
      this.style.setProperty('--vdt-story-brand-word-opacity', '0.72');
      this.style.setProperty('--vdt-story-brand-light-x', '-45%');
      this.style.setProperty('--vdt-story-stage-reveal', '0');
      this.style.setProperty('--vdt-story-tray-scale', this.storyTrayStartScale.toFixed(4));
      this.applyStoryReveal(0);

      const startedAt = window.performance.now();
      let logoComplete = false;
      const finishLogo = () => {
        if (logoComplete || this.storyMode !== 'desktop') return;
        logoComplete = true;
        if (this.storyLogoFrame) window.cancelAnimationFrame(this.storyLogoFrame);
        this.storyLogoFrame = null;
        if (this.storyLogoTimer) window.clearTimeout(this.storyLogoTimer);
        this.storyLogoTimer = null;
        this.style.setProperty('--vdt-story-brand-progress', '1');
        this.style.setProperty('--vdt-story-brand-scale', '1');
        this.style.setProperty('--vdt-story-brand-word-opacity', '1');
        this.style.setProperty('--vdt-story-brand-light-x', '145%');
        this.storyLogoElapsed = true;
        this.prepareDesktopDoor();
      };

      const animateLogo = (now) => {
        if (this.storyMode !== 'desktop' || !this.isConnected) return;
        const progress = clamp((now - startedAt) / this.storyLogoMs);
        this.style.setProperty('--vdt-story-brand-progress', progress.toFixed(4));
        this.style.setProperty('--vdt-story-brand-scale', (0.965 + progress * 0.035).toFixed(4));
        this.style.setProperty('--vdt-story-brand-word-opacity', (0.72 + progress * 0.28).toFixed(4));
        this.style.setProperty('--vdt-story-brand-light-x', `${(-45 + progress * 190).toFixed(2)}%`);

        if (progress < 1) {
          this.storyLogoFrame = window.requestAnimationFrame(animateLogo);
          return;
        }

        finishLogo();
      };

      this.storyLogoTimer = window.setTimeout(finishLogo, this.storyLogoMs + 120);
      this.storyLogoFrame = window.requestAnimationFrame(animateLogo);
    }

    prepareDesktopDoor() {
      if (
        this.storyMode !== 'desktop' ||
        !this.storyLogoElapsed ||
        !this.storyMetadataReady ||
        !this.video ||
        this.storyDesktopStarted
      ) {
        return;
      }

      this.seekStoryVideo(this.storyDoorTime, () => {
        if (this.storyMode !== 'desktop' || this.storyDesktopStarted) return;
        this.style.setProperty('--vdt-story-brand-opacity', '0');
        this.style.setProperty('--vdt-story-video-opacity', '1');
        this.classList.remove('is-story-brand');
        this.classList.add('is-story-door');
        if (this.doorTrigger) this.doorTrigger.hidden = false;
      });
    }

    seekStoryVideo(requestedTime, onReady) {
      const video = this.video;
      if (!video || video.readyState < 1) return;

      this.clearStorySeek();
      let complete = false;
      let frameCallbackId = null;
      let frameFallbackTimer = null;
      const startedAt = window.performance.now();
      const maximumTime = Number.isFinite(video.duration)
        ? Math.max(0, video.duration - 0.05)
        : requestedTime;
      const targetTime = clamp(requestedTime, 0, maximumTime);

      const removeWaiters = () => {
        video.removeEventListener('seeked', checkFrame);
        video.removeEventListener('loadeddata', checkFrame);
        video.removeEventListener('canplay', checkFrame);
        if (
          frameCallbackId !== null &&
          typeof video.cancelVideoFrameCallback === 'function'
        ) {
          video.cancelVideoFrameCallback(frameCallbackId);
        }
        frameCallbackId = null;
        if (frameFallbackTimer) window.clearTimeout(frameFallbackTimer);
        frameFallbackTimer = null;
        if (this.storySeekTimer) window.clearTimeout(this.storySeekTimer);
        this.storySeekTimer = null;
      };

      const finish = () => {
        if (complete) return;
        complete = true;
        removeWaiters();
        this.storySeekCleanup = null;
        if (typeof onReady === 'function') onReady();
      };

      const checkFrame = () => {
        if (complete || video !== this.video || this.storyMode !== 'desktop') return;
        if (frameCallbackId !== null) return;
        const frameIsReady =
          video.readyState >= 2 &&
          Math.abs(video.currentTime - targetTime) < 0.18;

        if (frameIsReady) {
          if (this.storySeekTimer) window.clearTimeout(this.storySeekTimer);
          this.storySeekTimer = null;
          if (typeof video.requestVideoFrameCallback === 'function') {
            frameCallbackId = video.requestVideoFrameCallback(() => finish());
            frameFallbackTimer = window.setTimeout(finish, 450);
          } else {
            finish();
          }
          return;
        }

        if (window.performance.now() - startedAt >= 6000) {
          complete = true;
          removeWaiters();
          this.storySeekCleanup = null;
          this.revealTray();
          return;
        }

        if (this.storySeekTimer) window.clearTimeout(this.storySeekTimer);
        this.storySeekTimer = window.setTimeout(checkFrame, 120);
      };

      video.addEventListener('seeked', checkFrame);
      video.addEventListener('loadeddata', checkFrame);
      video.addEventListener('canplay', checkFrame);
      this.storySeekTimer = window.setTimeout(checkFrame, 120);
      this.storySeekCleanup = () => {
        complete = true;
        removeWaiters();
      };

      try {
        video.pause();
        video.currentTime = targetTime;
        checkFrame();
      } catch (_error) {
        complete = true;
        removeWaiters();
        this.storySeekCleanup = null;
        this.revealTray();
      }
    }

    clearStorySeek() {
      if (this.storySeekCleanup) this.storySeekCleanup();
      this.storySeekCleanup = null;
      if (this.storySeekTimer) window.clearTimeout(this.storySeekTimer);
      this.storySeekTimer = null;
    }

    enterDesktopStory() {
      if (
        this.storyMode !== 'desktop' ||
        this.storyDesktopStarted ||
        !this.video ||
        !this.classList.contains('is-story-door')
      ) {
        return;
      }

      this.storyDesktopStarted = true;
      this.storyDoorHadFocus = Boolean(this.doorTrigger && document.activeElement === this.doorTrigger);
      this.classList.remove('is-story-door');
      this.classList.add('is-story-walking');
      if (this.doorTrigger) this.doorTrigger.hidden = true;

      const video = this.video;
      const monitorPlayback = () => {
        if (
          this.storyMode !== 'desktop' ||
          video !== this.video ||
          this.storyDesktopAwaitingTray ||
          this.storyDesktopRevealing
        ) return;
        if (video.ended || video.currentTime >= this.storyTrayTime - 0.04) {
          this.prepareDesktopTrayGate();
          return;
        }
        this.storyPlaybackFrame = window.requestAnimationFrame(monitorPlayback);
      };

      this.storyPlaybackFrame = window.requestAnimationFrame(monitorPlayback);
      this.storyWalkingTimer = window.setTimeout(
        () => this.prepareDesktopTrayGateAtTrayFrame(),
        Math.max(3500, (this.storyTrayTime - this.storyDoorTime + 4) * 1000)
      );

      let playPromise;
      try {
        playPromise = video.play();
      } catch (_error) {
        this.prepareDesktopTrayGateAtTrayFrame();
        return;
      }

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          window.setTimeout(() => this.prepareDesktopTrayGateAtTrayFrame(), 220);
        });
      }
    }

    prepareDesktopTrayGateAtTrayFrame() {
      if (
        this.storyMode !== 'desktop' ||
        this.storyDesktopAwaitingTray ||
        this.storyDesktopRevealing
      ) {
        return;
      }

      if (this.video) this.video.pause();
      if (this.storyPlaybackFrame) window.cancelAnimationFrame(this.storyPlaybackFrame);
      this.storyPlaybackFrame = null;
      if (this.storyWalkingTimer) window.clearTimeout(this.storyWalkingTimer);
      this.storyWalkingTimer = null;

      if (this.video && this.video.readyState >= 1) {
        this.seekStoryVideo(this.storyTrayTime, () => this.prepareDesktopTrayGate());
        return;
      }

      this.prepareDesktopTrayGate();
    }

    prepareDesktopTrayGate() {
      if (
        this.storyMode !== 'desktop' ||
        this.storyDesktopAwaitingTray ||
        this.storyDesktopRevealing
      ) {
        return;
      }

      this.storyDesktopAwaitingTray = true;
      if (this.video) this.video.pause();
      if (this.storyPlaybackFrame) window.cancelAnimationFrame(this.storyPlaybackFrame);
      this.storyPlaybackFrame = null;
      if (this.storyWalkingTimer) window.clearTimeout(this.storyWalkingTimer);
      this.storyWalkingTimer = null;

      this.classList.remove('is-story-walking');
      this.classList.add('is-story-tray-gate');
      if (this.doorTrigger) this.doorTrigger.hidden = true;
      if (this.trayTrigger) {
        this.trayTrigger.hidden = false;
        window.requestAnimationFrame(() => {
          if (
            this.storyMode === 'desktop' &&
            this.storyDesktopAwaitingTray &&
            this.trayTrigger &&
            !this.trayTrigger.hidden
          ) {
            this.trayTrigger.focus({ preventScroll: true });
          }
        });
      }
    }

    startDesktopTrayReveal() {
      if (
        this.storyMode !== 'desktop' ||
        !this.storyDesktopAwaitingTray ||
        this.storyDesktopRevealing
      ) return;
      this.storyDesktopAwaitingTray = false;
      this.storyDesktopRevealing = true;

      if (this.video) this.video.pause();
      if (this.storyPlaybackFrame) window.cancelAnimationFrame(this.storyPlaybackFrame);
      this.storyPlaybackFrame = null;
      if (this.storyWalkingTimer) window.clearTimeout(this.storyWalkingTimer);
      this.storyWalkingTimer = null;

      this.classList.remove('is-story-tray-gate');
      this.classList.add('is-story-stage-visible', 'is-story-revealing');
      this.style.setProperty('--vdt-story-tray-scale', this.storyTrayStartScale.toFixed(4));
      this.applyStoryReveal(0);
      const startedAt = window.performance.now();

      const revealFrame = (now) => {
        if (this.storyMode !== 'desktop' || !this.isConnected) return;
        const progress = clamp((now - startedAt) / this.storyRevealMs);
        // Fade the portrait video out before the 16:9 tray appears. Keeping the
        // two differently framed trays visible at the same time creates a false
        // shake even when the real tray geometry is perfectly stable.
        const introFade = smoothstep(0, 0.2, progress);
        const stageReveal = smoothstep(0.24, 0.44, progress);
        const trayZoom = smoothstep(0.2, 1, progress);
        const trayScale = this.storyTrayStartScale + trayZoom * (1 - this.storyTrayStartScale);
        const itemProgress = clamp((progress - 0.26) / 0.74);

        this.style.setProperty('--vdt-story-stage-reveal', stageReveal.toFixed(4));
        this.style.setProperty('--vdt-story-intro-opacity', (1 - introFade).toFixed(4));
        this.style.setProperty('--vdt-story-video-scale', '1');
        this.style.setProperty('--vdt-story-tray-scale', trayScale.toFixed(4));
        this.applyStoryReveal(itemProgress);

        if (progress < 1) {
          this.storyRevealFrame = window.requestAnimationFrame(revealFrame);
          return;
        }

        this.storyRevealFrame = null;
        this.finishDesktopStory();
      };

      this.storyRevealFrame = window.requestAnimationFrame(revealFrame);
    }

    finishDesktopStory() {
      if (this.storyMode !== 'desktop') return;
      this.applyStoryReveal(1);
      this.style.setProperty('--vdt-story-stage-reveal', '1');
      this.style.setProperty('--vdt-story-intro-opacity', '0');
      this.style.setProperty('--vdt-story-video-scale', '1');
      this.style.setProperty('--vdt-story-tray-scale', '1');
      this.classList.add('is-tray-visible');

      // Hold the exact final transform for two painted frames before changing
      // interaction state. This avoids a one-frame compositor rebuild at the
      // end of the reveal and keeps hitbox measurement out of the animation.
      let settleFramesRemaining = 2;
      const settleDesktopStory = () => {
        if (this.storyMode !== 'desktop' || !this.isConnected) {
          this.storyRevealFrame = null;
          return;
        }

        settleFramesRemaining -= 1;
        if (settleFramesRemaining > 0) {
          this.storyRevealFrame = window.requestAnimationFrame(settleDesktopStory);
          return;
        }

        this.storyRevealFrame = null;
        this.classList.remove(
          'is-story-brand',
          'is-story-door',
          'is-story-walking',
          'is-story-tray-gate',
          'is-story-revealing'
        );

        const focusWasInIntro = Boolean(this.intro && this.intro.contains(document.activeElement));
        if (focusWasInIntro && this.stage) {
          this.stage.setAttribute('aria-hidden', 'false');
          this.stage.removeAttribute('inert');
          const firstDiamond = this.activeBatch() && this.activeBatch().querySelector('[data-vdt-diamond]');
          const visibleNextButton = this.nextButton && !this.nextButton.hidden ? this.nextButton : null;
          const focusTarget = firstDiamond || visibleNextButton || this.stage;
          if (focusTarget) focusTarget.focus({ preventScroll: true });
        }
        this.setStoryInteractive(true);
        this.markIntroSeen();
        this.storyDesktopRevealing = false;
        this.storyMode = 'complete';

        if (this.intro) this.intro.setAttribute('aria-hidden', 'true');
        if (this.doorTrigger) this.doorTrigger.hidden = true;
        if (this.trayTrigger) this.trayTrigger.hidden = true;

        this.introCleanupTimer = window.setTimeout(() => {
          if (this.intro) this.intro.hidden = true;
          this.unloadVideo();
        }, this.transitionMs + 100);
      };

      this.storyRevealFrame = window.requestAnimationFrame(settleDesktopStory);
    }

    measureMobileVisualViewport() {
      const fallbackHeight = Math.max(
        1,
        window.innerHeight || document.documentElement.clientHeight || 1
      );
      const visualViewport = window.visualViewport;
      const visualHeight = Math.max(
        1,
        visualViewport && Number.isFinite(visualViewport.height)
          ? visualViewport.height
          : fallbackHeight
      );
      const visualTop = visualViewport && Number.isFinite(visualViewport.offsetTop)
        ? Math.max(0, visualViewport.offsetTop)
        : 0;
      const pinRect = (this.storyPin || this).getBoundingClientRect();
      const pinTop = Number.isFinite(pinRect.top) ? pinRect.top : visualTop;
      const topOffset = clamp((pinTop - visualTop) / visualHeight, 0, 1) * visualHeight;

      // The section starts below Shopify's announcement/header area. Use the
      // actually visible part of the visual viewport so bottom controls cannot
      // be placed beneath browser chrome while vertical story gestures are held.
      return {
        height: Math.max(1, Math.floor(visualHeight - topOffset)),
        topOffset: Math.max(0, Math.round(topOffset))
      };
    }

    applyMobileVisualViewport(measurement = this.measureMobileVisualViewport()) {
      if (!this.hasMobileStoryLayout() || !this.isConnected) return false;
      const height = Math.max(1, Number(measurement.height) || 1);
      const topOffset = Math.max(0, Number(measurement.topOffset) || 0);
      const changed =
        Math.abs(height - this.storyMobileViewportHeight) >= 1 ||
        Math.abs(topOffset - this.storyMobileVisualViewportOffset) >= 1;
      if (!changed) return false;

      const visualViewport = window.visualViewport;
      const visibleTop = visualViewport && Number.isFinite(visualViewport.offsetTop)
        ? visualViewport.offsetTop
        : 0;
      const visibleBottom = visibleTop + (
        visualViewport && Number.isFinite(visualViewport.height)
          ? visualViewport.height
          : window.innerHeight
      );
      const pinBeforeResize = (this.storyPin || this).getBoundingClientRect();
      const completedTrayWasVisible = Boolean(
        this.storyMode === 'complete' &&
        this.storyMobileTrayRevealComplete &&
        pinBeforeResize.bottom > visibleTop + 1 &&
        pinBeforeResize.top < visibleBottom - 1
      );

      this.storyMobileViewportHeight = height;
      this.storyMobileVisualViewportOffset = topOffset;
      this.style.setProperty('--vdt-mobile-viewport-px', `${height}px`);
      this.style.setProperty('--vdt-mobile-top-offset-px', `${topOffset}px`);
      if (completedTrayWasVisible) {
        window.requestAnimationFrame(() => {
          if (this.hasMobileStoryLayout() && this.storyMobileTrayRevealComplete) {
            this.syncMobileStoryScroll(MOBILE_STORY_REVEAL_PROGRESS);
          }
        });
      } else if (!this.storyMobileStepFrame) {
        this.renderMobileStoryProgress(this.storyMobileRenderedProgress);
      }
      return true;
    }

    scheduleMobileVisualViewportSync(immediate = false) {
      if (!this.hasMobileStoryLayout()) return;
      if (this.storyMobileVisualViewportFrame) {
        if (!immediate) return;
        window.cancelAnimationFrame(this.storyMobileVisualViewportFrame);
        this.storyMobileVisualViewportFrame = null;
      }
      if (immediate && this.storyMobileVisualViewportTimer) {
        window.clearTimeout(this.storyMobileVisualViewportTimer);
        this.storyMobileVisualViewportTimer = null;
      }

      const queueStableSync = () => {
        if (this.storyMobileVisualViewportTimer) {
          window.clearTimeout(this.storyMobileVisualViewportTimer);
        }
        const commitWhenStable = () => {
          this.storyMobileVisualViewportTimer = null;
          if (!this.hasMobileStoryLayout() || !this.isConnected) return;
          const next = this.measureMobileVisualViewport();
          if (this.storyMobileStepFrame && next.height > this.storyMobileViewportHeight) {
            this.storyMobileVisualViewportTimer = window.setTimeout(commitWhenStable, 120);
            return;
          }
          this.applyMobileVisualViewport(next);
        };
        this.storyMobileVisualViewportTimer = window.setTimeout(commitWhenStable, 160);
      };

      this.storyMobileVisualViewportFrame = window.requestAnimationFrame(() => {
        this.storyMobileVisualViewportFrame = null;
        if (!this.hasMobileStoryLayout() || !this.isConnected) return;
        const next = this.measureMobileVisualViewport();
        const viewportShrank =
          !this.storyMobileViewportHeight || next.height < this.storyMobileViewportHeight - 1;
        if (immediate || viewportShrank) this.applyMobileVisualViewport(next);
        if (!immediate) queueStableSync();
      });
    }

    setupMobileStory() {
      this.storyMode = 'mobile';
      this.storyDesktopAwaitingTray = false;
      this.storyMobileAwaitingTray = false;
      this.storyMobileTrayRevealStarted = false;
      this.storyMobileTrayRevealComplete = false;
      this.storyMobileWalkingPlayback = false;
      this.storyMobilePlaybackToken += 1;
      this.storyMobileRenderedProgress = 0;
      this.storyMobileAppliedRevealProgress = 0;
      this.storyMobileInternalScroll = false;
      this.storyMobileWheelGestureActive = false;
      this.storyMobileChapterIndex = 0;
      this.storyMobileTargetChapterIndex = null;
      this.storyMobilePendingDirections = [];
      this.classList.add('is-story-mobile', 'is-story-brand');
      this.classList.remove(
        'is-story-desktop',
        'is-story-door',
        'is-story-walking',
        'is-story-tray-gate',
        'is-story-mobile-tray-gate',
        'is-story-mobile-revealing'
      );
      this.scheduleMobileVisualViewportSync(true);
      if (this.doorTrigger) this.doorTrigger.hidden = true;
      if (this.trayTrigger) this.trayTrigger.hidden = true;
      this.style.removeProperty('--vdt-story-tray-scale');

      if (this.intro) this.intro.style.setProperty('--vdt-story-intro-opacity', '1');
      if (this.videoHost) {
        this.videoHost.style.setProperty('--vdt-story-video-opacity', '0');
        this.videoHost.style.setProperty('--vdt-story-video-scale', '1');
      }
      if (this.brandIntro) {
        this.brandIntro.style.setProperty('--vdt-story-brand-opacity', '1');
        this.brandIntro.style.setProperty('--vdt-story-brand-progress', '0');
        this.brandIntro.style.setProperty('--vdt-story-brand-scale', '0.965');
        this.brandIntro.style.setProperty('--vdt-story-brand-word-opacity', '0.72');
      }
      if (this.brandLight) this.brandLight.style.setProperty('--vdt-story-brand-light-shift', '-45vw');
      if (this.stage) this.stage.style.setProperty('--vdt-story-stage-reveal', '0');
      if (this.storyProgress) this.storyProgress.style.setProperty('--vdt-story-progress', '0');
      if (this.scrollCue) this.scrollCue.style.setProperty('--vdt-story-cue-opacity', '1');
      this.applyStoryReveal(0);

      const primeVideo = () => this.primeMobileStoryVideo();
      this.addEventListener('touchstart', (event) => {
        primeVideo();
        this.beginMobileStoryTouch(event);
      }, {
        passive: true,
        signal: this.abortController.signal
      });
      this.addEventListener('touchmove', (event) => this.moveMobileStoryTouch(event), {
        passive: false,
        signal: this.abortController.signal
      });
      this.addEventListener('touchend', () => this.endMobileStoryTouch(), {
        passive: true,
        signal: this.abortController.signal
      });
      this.addEventListener('touchcancel', () => this.endMobileStoryTouch(), {
        passive: true,
        signal: this.abortController.signal
      });
      this.addEventListener('wheel', (event) => this.handleMobileStoryWheel(event), {
        passive: false,
        signal: this.abortController.signal
      });
      this.addEventListener('pointerdown', primeVideo, {
        passive: true,
        signal: this.abortController.signal
      });

      this.renderMobileStoryProgress(0);
    }

    scheduleMobileStoryUpdate() {
      if (this.storyMode !== 'mobile' || this.storyScrollFrame) return;
      this.storyScrollFrame = window.requestAnimationFrame(() => {
        this.storyScrollFrame = null;
        this.updateMobileStory();
      });
    }

    mobileStoryMetrics() {
      const viewportHeight = Math.max(
        1,
        this.storyMobileViewportHeight || window.innerHeight || document.documentElement.clientHeight || 1
      );
      const rect = this.getBoundingClientRect();
      const travel = Math.max(1, this.offsetHeight - viewportHeight);
      return {
        progress: clamp(-rect.top / travel),
        travel,
        documentTop: window.scrollY + rect.top
      };
    }

    beginMobileStoryTouch(event) {
      if (this.storyMode !== 'mobile' || !event.touches || event.touches.length !== 1) {
        this.endMobileStoryTouch();
        return;
      }

      const touch = event.touches[0];
      const target = event.target instanceof Element ? event.target : null;
      this.storyMobileTouchIdentifier = touch.identifier;
      this.storyMobileTouchStartX = touch.clientX;
      this.storyMobileTouchStartY = touch.clientY;
      this.storyMobileTouchAxis = '';
      this.storyMobileTouchCommitted = false;
      this.storyMobileTouchBlocked = Boolean(target && target.closest(
        '[data-vdt-skip], [data-vdt-sound], [data-vdt-controls], [data-vdt-diamond], a, button, input, select, textarea'
      ));
    }

    moveMobileStoryTouch(event) {
      if (
        this.storyMode !== 'mobile' ||
        this.storyMobileTouchBlocked ||
        this.storyMobileTouchIdentifier === null
      ) return;

      const touch = Array.from(event.touches || []).find(
        (item) => item.identifier === this.storyMobileTouchIdentifier
      );
      if (!touch) return;

      const deltaX = this.storyMobileTouchStartX - touch.clientX;
      const deltaY = this.storyMobileTouchStartY - touch.clientY;
      if (!this.storyMobileTouchAxis) {
        if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < MOBILE_STORY_AXIS_THRESHOLD) return;
        this.storyMobileTouchAxis = Math.abs(deltaY) >= Math.abs(deltaX) ? 'vertical' : 'horizontal';
      }
      if (this.storyMobileTouchAxis !== 'vertical') return;

      const direction = deltaY >= 0 ? 1 : -1;
      const currentProgress = this.storyMobileRenderedProgress;
      const atReleasedBoundary =
        (direction < 0 && currentProgress <= 0.005) ||
        (
          direction > 0 &&
          this.storyMobileTrayRevealComplete &&
          currentProgress >= MOBILE_STORY_COMPLETE_PROGRESS
        );
      if (atReleasedBoundary) return;

      if (event.cancelable) event.preventDefault();
      if (
        this.storyMobileTouchCommitted ||
        Math.abs(deltaY) < MOBILE_STORY_SWIPE_THRESHOLD
      ) return;

      this.storyMobileTouchCommitted = true;
      this.requestMobileStoryStep(direction);
    }

    endMobileStoryTouch() {
      this.storyMobileTouchIdentifier = null;
      this.storyMobileTouchAxis = '';
      this.storyMobileTouchCommitted = false;
      this.storyMobileTouchBlocked = false;
    }

    handleMobileStoryWheel(event) {
      if (
        this.storyMode !== 'mobile' ||
        Math.abs(event.deltaY) <= Math.abs(event.deltaX) ||
        Math.abs(event.deltaY) < 4
      ) return;

      const direction = event.deltaY >= 0 ? 1 : -1;
      const currentProgress = this.storyMobileRenderedProgress;
      const atReleasedBoundary =
        (direction < 0 && currentProgress <= 0.005) ||
        (
          direction > 0 &&
          this.storyMobileTrayRevealComplete &&
          currentProgress >= MOBILE_STORY_COMPLETE_PROGRESS
        );
      if (atReleasedBoundary) return;

      if (event.cancelable) event.preventDefault();
      if (this.storyMobileWheelTimer) window.clearTimeout(this.storyMobileWheelTimer);
      this.storyMobileWheelTimer = window.setTimeout(() => {
        this.storyMobileWheelTimer = null;
        this.storyMobileWheelGestureActive = false;
      }, MOBILE_STORY_WHEEL_IDLE_MS);

      const gestureWasAlreadyActive = this.storyMobileWheelGestureActive;
      this.storyMobileWheelGestureActive = true;
      if (gestureWasAlreadyActive) return;

      this.requestMobileStoryStep(direction);
    }

    requestMobileStoryStep(direction) {
      if (
        this.storyMode !== 'mobile' ||
        !this.isConnected ||
        (direction !== 1 && direction !== -1) ||
        this.storyMobileRevealSettleFrame ||
        (direction > 0 && this.storyMobileAwaitingTray)
      ) return false;

      if (!this.storyMobileStepFrame && !this.storyMobileWaitingForFirstFrame) {
        return this.startMobileStoryStep(direction);
      }

      // Never discard an intentional second or third swipe. Queue at most the
      // two remaining chapters so rapid swipes still play every transition in
      // order, without allowing an inertial burst to skip any UI state.
      if (this.storyMobilePendingDirections.length >= 2) return false;
      let projectedIndex = Number.isInteger(this.storyMobileTargetChapterIndex)
        ? this.storyMobileTargetChapterIndex
        : this.storyMobileChapterIndex;
      this.storyMobilePendingDirections.forEach((queuedDirection) => {
        projectedIndex += queuedDirection;
      });
      const nextProjectedIndex = projectedIndex + direction;
      if (nextProjectedIndex < 0 || nextProjectedIndex >= MOBILE_STORY_STEPS.length) return false;
      this.storyMobilePendingDirections.push(direction);
      return true;
    }

    drainMobileStoryStepQueue() {
      if (
        this.storyMode !== 'mobile' ||
        this.storyMobileStepFrame ||
        this.storyMobileWaitingForFirstFrame ||
        this.storyMobileRevealSettleFrame ||
        this.storyMobileAwaitingTray
      ) return;

      while (this.storyMobilePendingDirections.length) {
        const direction = this.storyMobilePendingDirections.shift();
        if (this.startMobileStoryStep(direction)) return;
      }
    }

    startMobileStoryStep(direction, options = {}) {
      if (
        this.storyMode !== 'mobile' ||
        !this.isConnected ||
        this.storyMobileStepFrame ||
        (direction !== 1 && direction !== -1)
      ) return false;

      const currentProgress = clamp(this.storyMobileRenderedProgress);
      let targetIndex = null;
      let targetStep = null;
      let adjacentStep = null;
      const forcedProgress = Number(options.targetProgress);

      if (
        direction > 0 &&
        this.storyMobileAwaitingTray &&
        options.bypassTrayGate !== true
      ) return false;

      if (direction < 0 && this.storyMobileAwaitingTray) {
        this.closeMobileTrayGate();
      }

      if (Number.isFinite(forcedProgress)) {
        targetStep = {
          progress: clamp(forcedProgress),
          duration: Number(options.duration) || 0
        };
        adjacentStep = { progress: currentProgress, duration: targetStep.duration };
      } else {
        targetIndex = this.storyMobileChapterIndex + direction;
        if (targetIndex < 0 || targetIndex >= MOBILE_STORY_STEPS.length) return false;
        targetStep = MOBILE_STORY_STEPS[targetIndex];
        adjacentStep = MOBILE_STORY_STEPS[this.storyMobileChapterIndex];
      }

      // Never fade the brand away into an undecoded black video surface. A
      // gesture may prime playback, but the chapter itself waits until the
      // browser has decoded and painted its first frame. The global watchdog
      // remains active during this wait and falls back to the usable tray.
      if (targetStep.progress >= 0.1 && !this.storyFirstFrameReady) {
        this.primeMobileStoryVideo();
        this.storyMobileWaitingForFirstFrame = true;
        this.storyMobileDeferredStep = {
          direction,
          options: { ...options }
        };
        this.storyMobileTargetChapterIndex = Number.isInteger(targetIndex) ? targetIndex : null;
        return true;
      }

      const fullSpan = Math.max(0.001, Math.abs(targetStep.progress - adjacentStep.progress));
      const remainingSpan = Math.abs(targetStep.progress - currentProgress);
      const forwardDuration = direction > 0
        ? targetStep.duration
        : MOBILE_STORY_STEPS[this.storyMobileChapterIndex].duration * 0.8;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const configuredDuration = Number(options.duration);
      const duration = reducedMotion
        ? 1
        : Number.isFinite(configuredDuration)
          ? Math.max(420, configuredDuration)
          : clamp(forwardDuration * (remainingSpan / fullSpan), 420, Math.max(420, forwardDuration));
      const startedAt = window.performance.now();

      this.clearMobileStorySeekQueue();
      this.primeMobileStoryVideo();
      if (targetStep.progress >= 0.26) this.prepareMobileTrayAssets(false);
      this.classList.add('is-story-mobile-stepping');
      this.storyMobileTargetChapterIndex = Number.isInteger(targetIndex) ? targetIndex : null;
      const useWalkingPlayback =
        direction > 0 &&
        options.playVideo !== false &&
        targetStep.progress <= MOBILE_STORY_GATE_PROGRESS &&
        targetStep.progress > 0.17;
      if (useWalkingPlayback) {
        this.startMobileWalkingPlayback(currentProgress, targetStep.progress, duration);
      }

      const animateStep = (now) => {
        if (this.storyMode !== 'mobile' || !this.isConnected) {
          this.stopMobileWalkingPlayback();
          this.storyMobileStepFrame = null;
          this.storyMobileTargetChapterIndex = null;
          this.classList.remove('is-story-mobile-stepping');
          return;
        }

        const progress = clamp((now - startedAt) / duration);
        const eased = smoothstep(0, 1, progress);
        const renderedProgress = currentProgress + (targetStep.progress - currentProgress) * eased;
        this.renderMobileStoryProgress(renderedProgress);

        if (progress < 1) {
          this.storyMobileStepFrame = window.requestAnimationFrame(animateStep);
          return;
        }

        this.renderMobileStoryProgress(targetStep.progress);
        if (useWalkingPlayback) {
          this.stopMobileWalkingPlayback(targetStep.progress);
        } else {
          this.settleMobileStoryVideoTime(targetStep.progress);
        }
        this.storyMobileStepFrame = null;
        if (Number.isInteger(targetIndex)) this.storyMobileChapterIndex = targetIndex;
        this.storyMobileTargetChapterIndex = null;

        if (
          this.storyMobileTrayRevealStarted &&
          targetStep.progress >= MOBILE_STORY_REVEAL_PROGRESS
        ) {
          this.finishMobileTrayReveal();
          return;
        }

        this.classList.remove('is-story-mobile-stepping');
        if (
          direction > 0 &&
          targetStep.progress === MOBILE_STORY_GATE_PROGRESS &&
          !this.storyMobileTrayRevealStarted
        ) {
          this.openMobileTrayGate();
          return;
        }
        this.drainMobileStoryStepQueue();
      };

      this.storyMobileStepFrame = window.requestAnimationFrame(animateStep);
      return true;
    }

    syncMobileStoryScroll(progress) {
      if (!this.hasMobileStoryLayout() || !this.isConnected) return false;
      const metrics = this.mobileStoryMetrics();
      const targetScrollY = clamp(
        metrics.documentTop + clamp(progress) * metrics.travel,
        0,
        Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
      );
      if (Math.abs(window.scrollY - targetScrollY) < 0.75) return false;

      if (this.storyMobileSyncFrame) {
        window.cancelAnimationFrame(this.storyMobileSyncFrame);
        this.storyMobileSyncFrame = null;
      }
      this.storyMobileInternalScroll = true;
      const rootStyle = document.documentElement.style;
      if (this.storyMobileScrollBehaviorRestore) this.storyMobileScrollBehaviorRestore();
      const previousScrollBehavior = rootStyle.getPropertyValue('scroll-behavior');
      const previousScrollPriority = rootStyle.getPropertyPriority('scroll-behavior');
      const restoreScrollBehavior = () => {
        if (previousScrollBehavior) {
          rootStyle.setProperty('scroll-behavior', previousScrollBehavior, previousScrollPriority);
        } else {
          rootStyle.removeProperty('scroll-behavior');
        }
        if (this.storyMobileScrollBehaviorRestore === restoreScrollBehavior) {
          this.storyMobileScrollBehaviorRestore = null;
        }
      };
      this.storyMobileScrollBehaviorRestore = restoreScrollBehavior;
      rootStyle.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo({ top: targetScrollY, left: 0, behavior: 'auto' });

      let framesRemaining = 2;
      const releaseInternalScroll = () => {
        if (!this.isConnected) {
          this.storyMobileSyncFrame = null;
          this.storyMobileInternalScroll = false;
          restoreScrollBehavior();
          return;
        }
        framesRemaining -= 1;
        if (framesRemaining > 0) {
          this.storyMobileSyncFrame = window.requestAnimationFrame(releaseInternalScroll);
          return;
        }
        this.storyMobileSyncFrame = null;
        this.storyMobileInternalScroll = false;
        restoreScrollBehavior();
      };
      this.storyMobileSyncFrame = window.requestAnimationFrame(releaseInternalScroll);
      return true;
    }

    prepareMobileTrayAssets(includeDiamonds = false) {
      const batch = this.activeBatch();
      const background = this.querySelector('.vdt__background-image');
      if (background) {
        background.loading = 'eager';
        background.fetchPriority = includeDiamonds ? 'high' : 'low';
        if (typeof background.decode === 'function') background.decode().catch(() => {});
      }
      if (!includeDiamonds || this.storyTrayAssetPromise) return this.storyTrayAssetPromise;

      const diamonds = batch ? Array.from(batch.querySelectorAll('.vdt__diamond-image')) : [];
      this.storyTrayAssetPromise = diamonds.reduce((sequence, image) => sequence.then(() => {
        image.loading = 'eager';
        image.fetchPriority = 'low';
        return typeof image.decode === 'function' ? image.decode().catch(() => {}) : undefined;
      }), Promise.resolve());
      return this.storyTrayAssetPromise;
    }

    mobileStoryVideoTime(progress) {
      const walkingProgress = clamp((clamp(progress) - 0.16) / 0.48);
      return this.storyDoorTime + (this.storyTrayTime - this.storyDoorTime) * walkingProgress;
    }

    startMobileWalkingPlayback(startProgress, targetProgress, duration) {
      const video = this.video;
      if (
        !video ||
        this.storyMode !== 'mobile' ||
        !this.storyMetadataReady ||
        video.readyState < 1
      ) return false;

      this.clearMobileStorySeekQueue();
      const startTime = this.mobileStoryVideoTime(startProgress);
      const targetTime = this.mobileStoryVideoTime(targetProgress);
      const durationSeconds = Math.max(0.42, Number(duration) / 1000 || 0.42);
      const playbackRate = clamp((targetTime - startTime) / durationSeconds, 0.5, 2);
      const playbackToken = this.storyMobilePlaybackToken + 1;
      this.storyMobilePlaybackToken = playbackToken;
      this.storyMobileWalkingPlayback = true;
      this.storyDesiredTime = startTime;

      const fallBackToStaticFrame = () => {
        if (
          video !== this.video ||
          this.storyMode !== 'mobile' ||
          playbackToken !== this.storyMobilePlaybackToken
        ) return;
        this.storyMobileWalkingPlayback = false;
        try {
          video.pause();
          video.playbackRate = 1;
        } catch (_error) {
          // Seeking below is still a valid fallback when play/pause is restricted.
        }
        this.storyDesiredTime = targetTime;
        this.setStoryVideoTime(targetTime);
      };

      try {
        video.pause();
        video.playbackRate = playbackRate;
        if (!video.seeking && Math.abs(video.currentTime - startTime) >= 0.04) {
          video.currentTime = startTime;
        }
      } catch (_error) {
        fallBackToStaticFrame();
        return false;
      }

      let playPromise;
      try {
        playPromise = video.play();
      } catch (_error) {
        fallBackToStaticFrame();
        return false;
      }

      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          if (
            video !== this.video ||
            playbackToken !== this.storyMobilePlaybackToken
          ) return;
          fallBackToStaticFrame();
        });
      }
      return true;
    }

    settleMobileStoryVideoTime(targetProgress, tolerance = 0.14) {
      if (!Number.isFinite(targetProgress)) return;
      const desiredTime = this.mobileStoryVideoTime(targetProgress);
      this.storyDesiredTime = desiredTime;
      const video = this.video;
      if (
        !video ||
        !this.storyMetadataReady ||
        video.readyState < 1 ||
        Math.abs(video.currentTime - desiredTime) <= tolerance
      ) return;
      this.setStoryVideoTime(desiredTime);
    }

    stopMobileWalkingPlayback(targetProgress) {
      const video = this.video;
      this.storyMobilePlaybackToken += 1;
      this.storyMobileWalkingPlayback = false;
      if (video) {
        video.pause();
        try {
          video.playbackRate = 1;
        } catch (_error) {
          // Ignore playback-rate limitations in embedded mobile browsers.
        }
      }
      if (Number.isFinite(targetProgress)) {
        this.settleMobileStoryVideoTime(targetProgress);
      }
    }

    clearMobileTrayGateTimer() {
      if (this.storyMobileGateCleanup) this.storyMobileGateCleanup();
      this.storyMobileGateCleanup = null;
      if (this.storyMobileGateTimer) window.clearTimeout(this.storyMobileGateTimer);
      this.storyMobileGateTimer = null;
    }

    openMobileTrayGate() {
      if (
        this.storyMode !== 'mobile' ||
        this.storyMobileTrayRevealStarted ||
        this.storyMobileAwaitingTray
      ) return;

      this.storyMobileAwaitingTray = true;
      this.storyMobilePendingDirections = [];
      this.prepareMobileTrayAssets(true);
      this.stopMobileWalkingPlayback(MOBILE_STORY_GATE_PROGRESS);
      this.renderMobileStoryProgress(MOBILE_STORY_GATE_PROGRESS);
      this.classList.remove('is-story-walking');
      this.classList.add('is-story-tray-gate', 'is-story-mobile-tray-gate');
      if (this.trayTrigger) this.trayTrigger.hidden = true;

      // Expose the button only after the exact tray frame has been decoded and
      // painted. A fixed delay can expire while a WebView is still seeking and
      // leaves the customer looking at a black surface. If the frame never
      // arrives, fail open to the fully usable tray instead of trapping them.
      this.clearMobileTrayGateTimer();
      const video = this.video;
      let completed = false;
      let pollTimer = null;
      let deadlineTimer = null;
      let paintTimer = null;
      let firstPaintFrame = null;
      let secondPaintFrame = null;
      let frameCallbackId = null;
      let paintConfirmationPending = false;

      const storyIsCurrent = () => (
        this.storyMode === 'mobile' &&
        this.storyMobileAwaitingTray &&
        !this.storyMobileTrayRevealStarted
      );
      const frameIsReady = () => Boolean(
        video &&
        video === this.video &&
        video.readyState >= 2 &&
        !video.seeking &&
        Math.abs(video.currentTime - this.storyTrayTime) < 0.18
      );
      const removeWaiters = () => {
        if (video) {
          video.removeEventListener('seeked', inspectFrame);
          video.removeEventListener('loadeddata', inspectFrame);
          video.removeEventListener('canplay', inspectFrame);
        }
        if (
          frameCallbackId !== null &&
          video &&
          typeof video.cancelVideoFrameCallback === 'function'
        ) {
          try {
            video.cancelVideoFrameCallback(frameCallbackId);
          } catch (_error) {
            // The callback may already be executing.
          }
        }
        frameCallbackId = null;
        if (pollTimer) window.clearTimeout(pollTimer);
        pollTimer = null;
        if (deadlineTimer) window.clearTimeout(deadlineTimer);
        deadlineTimer = null;
        if (paintTimer) window.clearTimeout(paintTimer);
        paintTimer = null;
        if (firstPaintFrame) window.cancelAnimationFrame(firstPaintFrame);
        firstPaintFrame = null;
        if (secondPaintFrame) window.cancelAnimationFrame(secondPaintFrame);
        secondPaintFrame = null;
        this.storyMobileGateTimer = null;
      };
      const finishWait = () => {
        if (completed) return false;
        completed = true;
        removeWaiters();
        this.storyMobileGateCleanup = null;
        return true;
      };
      const showGate = () => {
        if (!storyIsCurrent() || !frameIsReady() || !finishWait()) return;
        if (this.trayTrigger) {
          this.trayTrigger.hidden = false;
          window.requestAnimationFrame(() => {
            if (
              this.storyMode === 'mobile' &&
              this.storyMobileAwaitingTray &&
              this.trayTrigger &&
              !this.trayTrigger.hidden
            ) {
              this.trayTrigger.focus({ preventScroll: true });
            }
          });
        }
      };
      const confirmPaint = () => {
        if (completed || paintConfirmationPending || !frameIsReady()) return;
        paintConfirmationPending = true;

        const painted = () => {
          if (completed) return;
          if (paintTimer) window.clearTimeout(paintTimer);
          paintTimer = null;
          const pendingFrameCallbackId = frameCallbackId;
          frameCallbackId = null;
          if (
            pendingFrameCallbackId !== null &&
            typeof video.cancelVideoFrameCallback === 'function'
          ) {
            try {
              video.cancelVideoFrameCallback(pendingFrameCallbackId);
            } catch (_error) {
              // The callback may be the one currently being delivered.
            }
          }
          paintConfirmationPending = false;
          if (frameIsReady()) {
            showGate();
          } else {
            inspectFrame();
          }
        };

        if (typeof video.requestVideoFrameCallback === 'function') {
          try {
            frameCallbackId = video.requestVideoFrameCallback(painted);
            paintTimer = window.setTimeout(painted, STORY_FRAME_PAINT_FALLBACK_MS);
            return;
          } catch (_error) {
            frameCallbackId = null;
          }
        }

        firstPaintFrame = window.requestAnimationFrame(() => {
          firstPaintFrame = null;
          secondPaintFrame = window.requestAnimationFrame(() => {
            secondPaintFrame = null;
            painted();
          });
        });
      };
      const inspectFrame = () => {
        if (completed) return;
        if (!storyIsCurrent()) {
          finishWait();
          return;
        }
        if (frameIsReady()) {
          confirmPaint();
          return;
        }
        if (pollTimer) window.clearTimeout(pollTimer);
        pollTimer = window.setTimeout(inspectFrame, 120);
        this.storyMobileGateTimer = pollTimer;
      };
      const fallBackToTray = () => {
        if (!storyIsCurrent() || !finishWait()) return;
        this.revealTray();
      };

      this.storyMobileGateCleanup = () => {
        if (completed) return;
        completed = true;
        removeWaiters();
        this.storyMobileGateCleanup = null;
      };
      if (!video) {
        fallBackToTray();
        return;
      }
      video.addEventListener('seeked', inspectFrame);
      video.addEventListener('loadeddata', inspectFrame);
      video.addEventListener('canplay', inspectFrame);
      deadlineTimer = window.setTimeout(fallBackToTray, STORY_FRAME_TIMEOUT_MS);
      inspectFrame();
    }

    closeMobileTrayGate() {
      this.clearMobileTrayGateTimer();
      this.storyMobileAwaitingTray = false;
      this.classList.remove('is-story-tray-gate', 'is-story-mobile-tray-gate');
      if (this.trayTrigger) this.trayTrigger.hidden = true;
    }

    startMobileTrayReveal() {
      if (
        this.storyMode !== 'mobile' ||
        !this.storyMobileAwaitingTray ||
        this.storyMobileTrayRevealStarted ||
        this.storyMobileStepFrame
      ) return;

      this.clearMobileTrayGateTimer();
      this.storyMobileAwaitingTray = false;
      this.storyMobileTrayRevealStarted = true;
      this.classList.remove('is-story-tray-gate', 'is-story-mobile-tray-gate');
      this.classList.add('is-story-stage-visible', 'is-story-revealing', 'is-story-mobile-revealing');
      if (this.trayTrigger) this.trayTrigger.hidden = true;
      this.renderMobileStoryProgress(MOBILE_STORY_GATE_PROGRESS);

      const revealStarted = this.startMobileStoryStep(1, {
        targetProgress: MOBILE_STORY_REVEAL_PROGRESS,
        duration: 2800,
        bypassTrayGate: true,
        playVideo: false
      });

      if (!revealStarted) {
        this.storyMobileTrayRevealStarted = false;
        this.classList.remove('is-story-stage-visible', 'is-story-revealing', 'is-story-mobile-revealing');
        this.openMobileTrayGate();
      }
    }

    finishMobileTrayReveal() {
      if (
        this.storyMode !== 'mobile' ||
        !this.storyMobileTrayRevealStarted ||
        this.storyMobileRevealSettleFrame
      ) return;

      this.renderMobileStoryProgress(MOBILE_STORY_REVEAL_PROGRESS);
      this.syncMobileStoryScroll(MOBILE_STORY_REVEAL_PROGRESS);
      this.classList.add('is-tray-visible', 'is-story-stage-visible');

      let settleFramesRemaining = 2;
      const settleMobileStory = () => {
        if (this.storyMode !== 'mobile' || !this.isConnected) {
          this.storyMobileRevealSettleFrame = null;
          return;
        }
        settleFramesRemaining -= 1;
        if (settleFramesRemaining > 0) {
          this.storyMobileRevealSettleFrame = window.requestAnimationFrame(settleMobileStory);
          return;
        }

        this.storyMobileRevealSettleFrame = null;
        this.storyMobileTrayRevealStarted = false;
        this.storyMobileTrayRevealComplete = true;
        this.classList.remove(
          'is-story-brand',
          'is-story-door',
          'is-story-walking',
          'is-story-tray-gate',
          'is-story-revealing',
          'is-story-mobile-stepping',
          'is-story-mobile-tray-gate',
          'is-story-mobile-revealing'
        );
        this.renderMobileStoryProgress(MOBILE_STORY_REVEAL_PROGRESS);
        this.setStoryInteractive(true);
        this.markIntroSeen();
        this.storyMode = 'complete';
        if (this.trayTrigger) this.trayTrigger.hidden = true;

        const focusWasOnTrigger = Boolean(
          this.trayTrigger && this.trayTrigger === document.activeElement
        );
        if (focusWasOnTrigger) {
          const firstDiamond = this.activeBatch() && this.activeBatch().querySelector('[data-vdt-diamond]');
          const focusTarget = firstDiamond || this.nextButton || this.stage;
          if (focusTarget) focusTarget.focus({ preventScroll: true });
        }

        this.clearMobileStorySeekQueue();
        this.introCleanupTimer = window.setTimeout(() => {
          if (this.intro) this.intro.hidden = true;
          this.unloadVideo();
        }, this.transitionMs + 100);
      };

      this.storyMobileRevealSettleFrame = window.requestAnimationFrame(settleMobileStory);
    }

    renderMobileStoryProgress(requestedProgress) {
      if (this.storyMode !== 'mobile' || !this.isConnected) return;
      const progress = clamp(requestedProgress);
      this.storyMobileRenderedProgress = progress;
      const revealingTray = this.storyMobileTrayRevealStarted || this.storyMobileTrayRevealComplete;
      const brandProgress = smoothstep(0, 0.12, progress);
      const brandOpacity = 1 - smoothstep(0.08, 0.17, progress);
      const videoEntrance = smoothstep(0.1, 0.18, progress);
      const cueOpacity = 1 - smoothstep(0.08, 0.25, progress);
      const walkingProgress = clamp((progress - 0.16) / 0.48);
      const trayProgress = revealingTray
        ? clamp(
          (progress - MOBILE_STORY_GATE_PROGRESS) /
          (MOBILE_STORY_REVEAL_PROGRESS - MOBILE_STORY_GATE_PROGRESS)
        )
        : 0;
      const introFade = revealingTray ? smoothstep(0, 0.2, trayProgress) : 0;
      const stageReveal = revealingTray ? smoothstep(0.24, 0.44, trayProgress) : 0;
      const videoOpacity = revealingTray ? 1 : videoEntrance;
      const storyReveal = revealingTray ? clamp((trayProgress - 0.26) / 0.74) : 0;

      // Scope animated variables to the element that consumes them. Updating
      // variables on the root invalidated styles for every diamond on each frame.
      if (this.storyProgress) {
        this.storyProgress.style.setProperty('--vdt-story-progress', progress.toFixed(4));
      }
      if (this.brandIntro) {
        this.brandIntro.style.setProperty('--vdt-story-brand-progress', brandProgress.toFixed(4));
        this.brandIntro.style.setProperty(
          '--vdt-story-brand-scale',
          (0.965 + brandProgress * 0.035).toFixed(4)
        );
        this.brandIntro.style.setProperty(
          '--vdt-story-brand-word-opacity',
          (0.72 + brandProgress * 0.28).toFixed(4)
        );
        this.brandIntro.style.setProperty('--vdt-story-brand-opacity', brandOpacity.toFixed(4));
      }
      if (this.brandLight) {
        this.brandLight.style.setProperty(
          '--vdt-story-brand-light-shift',
          `${(-45 + brandProgress * 190).toFixed(2)}vw`
        );
      }
      if (this.videoHost) {
        this.videoHost.style.setProperty('--vdt-story-video-opacity', videoOpacity.toFixed(4));
        this.videoHost.style.setProperty(
          '--vdt-story-video-scale',
          (1 + walkingProgress * 0.025).toFixed(4)
        );
      }
      if (this.stage) this.stage.style.setProperty('--vdt-story-stage-reveal', stageReveal.toFixed(4));
      if (this.intro) {
        this.intro.style.setProperty('--vdt-story-intro-opacity', (1 - introFade).toFixed(4));
      }
      if (this.scrollCue) {
        this.scrollCue.style.setProperty('--vdt-story-cue-opacity', cueOpacity.toFixed(4));
      }

      this.classList.toggle('is-story-brand', progress < 0.17);
      this.classList.toggle(
        'is-story-door',
        !revealingTray && progress >= 0.17 && progress < 0.26
      );
      this.classList.toggle(
        'is-story-walking',
        !revealingTray &&
        progress >= 0.26 &&
        progress < MOBILE_STORY_GATE_PROGRESS &&
        !this.storyMobileAwaitingTray
      );
      this.classList.toggle('is-story-stage-visible', revealingTray);

      const desiredTime = this.mobileStoryVideoTime(progress);
      this.storyDesiredTime = desiredTime;
      if (
        !this.storyMobileStepFrame &&
        !this.storyMobileWalkingPlayback &&
        !this.storyMobileAwaitingTray &&
        !this.storyMobileTrayRevealStarted &&
        !this.storyMobileTrayRevealComplete
      ) {
        this.setStoryVideoTime(desiredTime);
      }
      if (
        this.storyMobileAppliedRevealProgress === null ||
        Math.abs(this.storyMobileAppliedRevealProgress - storyReveal) >= 0.003
      ) {
        this.applyStoryReveal(storyReveal);
        this.storyMobileAppliedRevealProgress = storyReveal;
      }
      this.setStoryInteractive(
        this.storyMobileTrayRevealComplete && progress >= MOBILE_STORY_COMPLETE_PROGRESS
      );
    }

    updateMobileStory() {
      if (
        this.storyMode !== 'mobile' ||
        !this.isConnected ||
        this.storyMobileStepFrame ||
        this.storyMobileInternalScroll ||
        this.storyMobileRevealSettleFrame
      ) return;

      // Chapter progress is virtual and deterministic. Physical document scroll
      // is intentionally ignored until the reveal completes, preventing browser
      // address-bar movement or theme scroll listeners from moving the timeline.
      this.renderMobileStoryProgress(this.storyMobileRenderedProgress);
    }

    primeMobileStoryVideo() {
      const video = this.video;
      if (!video || this.storyMode !== 'mobile' || this.storyVideoPrimed) return;
      this.storyVideoPrimed = true;

      let playPromise;
      try {
        playPromise = video.play();
      } catch (_error) {
        this.storyVideoPrimed = false;
        return;
      }

      const pauseAtStoryFrame = () => {
        if (video !== this.video) return;
        if (this.storyMobileWalkingPlayback) return;
        video.pause();
        if (!this.storyMobileStepFrame) this.setStoryVideoTime(this.storyDesiredTime);
      };

      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.then(pauseAtStoryFrame, () => {
          this.storyVideoPrimed = false;
        });
      } else {
        pauseAtStoryFrame();
      }
    }

    setStoryVideoTime(requestedTime) {
      this.storyDesiredTime = requestedTime;
      const video = this.video;
      if (!video || !this.storyMetadataReady || video.readyState < 1) return;

      if (video.seeking) {
        this.queueMobileStorySeek(video);
        return;
      }

      const maximumTime = Number.isFinite(video.duration)
        ? Math.max(0, video.duration - 0.05)
        : requestedTime;
      const targetTime = clamp(requestedTime, 0, maximumTime);
      if (Math.abs(video.currentTime - targetTime) < 0.025) return;

      try {
        if (!video.paused) video.pause();
        video.currentTime = targetTime;
        this.queueMobileStorySeek(video);
      } catch (_error) {
        // Some iOS/WeChat builds defer seeking until the first touch. The
        // priming gesture above retries the latest requested frame.
      }
    }

    queueMobileStorySeek(video) {
      if (!video || this.storyMobileSeekCleanup) return;

      const flushLatestFrame = () => {
        if (this.storyMobileSeekCleanup) this.storyMobileSeekCleanup();
        if (this.storyMode === 'mobile' && video === this.video && !video.seeking) {
          this.setStoryVideoTime(this.storyDesiredTime);
        }
      };

      video.addEventListener('seeked', flushLatestFrame, { once: true });
      this.storyMobileSeekTimer = window.setTimeout(() => {
        this.storyMobileSeekTimer = null;
        if (!video.seeking) flushLatestFrame();
      }, 900);
      this.storyMobileSeekCleanup = () => {
        video.removeEventListener('seeked', flushLatestFrame);
        if (this.storyMobileSeekTimer) window.clearTimeout(this.storyMobileSeekTimer);
        this.storyMobileSeekTimer = null;
        this.storyMobileSeekCleanup = null;
      };
    }

    clearMobileStorySeekQueue() {
      if (this.storyMobileSeekCleanup) this.storyMobileSeekCleanup();
      this.storyMobileSeekCleanup = null;
      if (this.storyMobileSeekTimer) window.clearTimeout(this.storyMobileSeekTimer);
      this.storyMobileSeekTimer = null;
    }

    applyStoryReveal(progress) {
      const normalizedProgress = clamp(progress);
      const batch = this.activeBatch();
      if (!this.storyRevealCache || this.storyRevealCache.batch !== batch) {
        this.storyRevealCache = {
          batch,
          diamonds: batch
            ? Array.from(batch.querySelectorAll('[data-vdt-diamond]')).sort(
              (first, second) => (Number(first.dataset.slot) || 0) - (Number(second.dataset.slot) || 0)
            )
            : []
        };
      }
      const diamonds = this.storyRevealCache.diamonds;
      const useMobileCompositorPath = this.storyMode === 'mobile';

      diamonds.forEach((diamond, index) => {
        const start = index * 0.055;
        const gemProgress = smoothstep(start, start + 0.24, normalizedProgress);
        const plateProgress = smoothstep(start + 0.12, start + 0.35, normalizedProgress);
        const gemScale = 0.72 + gemProgress * 0.28;

        diamond.style.setProperty('--vdt-story-gem-opacity', gemProgress.toFixed(4));
        diamond.style.setProperty('--vdt-story-gem-scale', gemScale.toFixed(4));
        diamond.style.setProperty('--vdt-story-gem-lift', `${((1 - gemProgress) * 18).toFixed(2)}px`);
        diamond.style.setProperty('--vdt-story-plate-opacity', plateProgress.toFixed(4));
        diamond.style.setProperty('--vdt-story-plate-shift', `${((1 - plateProgress) * 12).toFixed(2)}px`);
        if (!useMobileCompositorPath) {
          diamond.style.setProperty('--vdt-story-gem-blur', `${((1 - gemProgress) * 8).toFixed(2)}px`);
          diamond.style.setProperty('--vdt-story-plate-blur', `${((1 - plateProgress) * 5).toFixed(2)}px`);
        }
      });

      const controlsProgress = smoothstep(0.76, 0.98, normalizedProgress);
      const controlsTarget = useMobileCompositorPath && this.storyControls
        ? this.storyControls
        : this;
      controlsTarget.style.setProperty('--vdt-story-controls-reveal', controlsProgress.toFixed(4));
      controlsTarget.style.setProperty(
        '--vdt-story-controls-shift',
        `${((1 - controlsProgress) * 18).toFixed(2)}px`
      );
    }

    clearStoryItemStyles() {
      this.querySelectorAll('[data-vdt-diamond]').forEach((diamond) => {
        [
          '--vdt-story-gem-opacity',
          '--vdt-story-gem-scale',
          '--vdt-story-gem-lift',
          '--vdt-story-gem-blur',
          '--vdt-story-plate-opacity',
          '--vdt-story-plate-shift',
          '--vdt-story-plate-blur'
        ].forEach((property) => diamond.style.removeProperty(property));
      });
    }

    setStoryInteractive(isInteractive) {
      const active = Boolean(isInteractive);
      const stateAlreadyApplied =
        this.storyInteractive === active &&
        this.classList.contains('is-story-interactive') === active;
      if (stateAlreadyApplied) return;

      this.storyInteractive = active;
      this.classList.toggle('is-story-interactive', active);

      if (this.stage) {
        this.stage.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (active) {
          this.stage.removeAttribute('inert');
        } else {
          this.stage.setAttribute('inert', '');
        }
      }

      if (this.intro) {
        this.intro.setAttribute('aria-hidden', active ? 'true' : 'false');
        if (active) {
          this.intro.setAttribute('inert', '');
        } else {
          this.intro.removeAttribute('inert');
        }
      }
      if (active) {
        this.markIntroSeen();
        this.scheduleDiamondHitboxSync();
      }
    }

    clearStoryRuntime() {
      [
        'storyLogoFrame',
        'storyPlaybackFrame',
        'storyRevealFrame',
        'storyScrollFrame',
        'storyMobileStepFrame',
        'storyMobileSyncFrame',
        'storyMobileRevealSettleFrame',
        'storyMobileVisualViewportFrame'
      ].forEach((property) => {
        if (this[property]) window.cancelAnimationFrame(this[property]);
        this[property] = null;
      });
      if (this.storyMobileScrollBehaviorRestore) this.storyMobileScrollBehaviorRestore();

      [
        'storyLogoTimer',
        'storyWalkingTimer',
        'storyMobileWheelTimer',
        'storyMobileGateTimer',
        'storyMobileVisualViewportTimer'
      ].forEach((property) => {
        if (this[property]) window.clearTimeout(this[property]);
        this[property] = null;
      });

      this.clearStorySeek();
      this.clearMobileStorySeekQueue();
      this.clearMobileTrayGateTimer();
      this.clearStoryFirstFrameGate();
      if (this.storyScrollHandler) window.removeEventListener('scroll', this.storyScrollHandler);
      this.storyScrollHandler = null;
      this.storyMode = '';
      this.storyInteractive = false;
      this.storyMetadataReady = false;
      this.storyFirstFrameReady = false;
      this.storyDesktopStarted = false;
      this.storyDesktopAwaitingTray = false;
      this.storyDesktopRevealing = false;
      this.storyDoorHadFocus = false;
      this.storyVideoPrimed = false;
      this.stopMobileWalkingPlayback();
      this.storyMobileAwaitingTray = false;
      this.storyMobileTrayRevealStarted = false;
      this.storyMobileTrayRevealComplete = false;
      this.storyMobileRenderedProgress = 0;
      this.storyMobileAppliedRevealProgress = null;
      this.storyMobileInternalScroll = false;
      this.storyMobileWheelGestureActive = false;
      this.storyMobileChapterIndex = 0;
      this.storyMobileTargetChapterIndex = null;
      this.storyMobilePendingDirections = [];
      this.storyMobileWaitingForFirstFrame = false;
      this.storyMobileDeferredStep = null;
      this.storyMobileViewportHeight = 0;
      this.storyMobileVisualViewportOffset = 0;
      this.storyTrayAssetPromise = null;
      this.storyRevealCache = null;
      this.endMobileStoryTouch();
      this.classList.remove(
        'is-story-tray-gate',
        'is-story-revealing',
        'is-story-mobile-stepping',
        'is-story-mobile-tray-gate',
        'is-story-mobile-revealing'
      );
      if (this.trayTrigger) this.trayTrigger.hidden = true;
      this.style.removeProperty('--vdt-story-tray-scale');
      this.style.removeProperty('--vdt-mobile-viewport-px');
      this.style.removeProperty('--vdt-mobile-top-offset-px');
      [
        this.intro,
        this.videoHost,
        this.brandIntro,
        this.brandLight,
        this.scrollCue,
        this.storyProgress,
        this.stage,
        this.storyControls
      ].filter(Boolean).forEach((element) => {
        [
          '--vdt-story-intro-opacity',
          '--vdt-story-video-opacity',
          '--vdt-story-video-scale',
          '--vdt-story-brand-opacity',
          '--vdt-story-brand-progress',
          '--vdt-story-brand-scale',
          '--vdt-story-brand-word-opacity',
          '--vdt-story-brand-light-shift',
          '--vdt-story-cue-opacity',
          '--vdt-story-progress',
          '--vdt-story-stage-reveal',
          '--vdt-story-controls-reveal',
          '--vdt-story-controls-shift'
        ].forEach((property) => element.style.removeProperty(property));
      });
    }

    clearIntroPlaybackRecovery() {
      if (this.introPlaybackCleanup) this.introPlaybackCleanup();
      this.introPlaybackCleanup = null;
      this.introPlayPending = false;
    }

    revealTray(options = {}) {
      if (this.nativeStoryCleanup) { this.nativeStoryCleanup(); this.nativeStoryCleanup = null; }
      const focusWasInIntro = Boolean(this.intro && this.intro.contains(document.activeElement));
      const wasMobileStory = this.classList.contains('is-story-mobile');
      this.markIntroSeen();
      this.clearTimers();
      this.classList.remove(
        'is-story-active',
        'is-story-desktop',
        'is-story-mobile',
        'is-story-brand',
        'is-story-door',
        'is-story-walking',
        'is-story-tray-gate',
        'is-story-revealing',
        'is-story-stage-visible',
        'is-story-interactive'
      );
      this.classList.add('is-tray-visible');
      [
        '--vdt-story-intro-opacity',
        '--vdt-story-video-opacity',
        '--vdt-story-video-scale',
        '--vdt-story-brand-opacity',
        '--vdt-story-brand-progress',
        '--vdt-story-brand-scale',
        '--vdt-story-brand-word-opacity',
        '--vdt-story-brand-light-x',
        '--vdt-story-stage-reveal',
        '--vdt-story-tray-scale',
        '--vdt-story-progress',
        '--vdt-story-cue-opacity',
        '--vdt-story-controls-reveal',
        '--vdt-story-controls-shift'
      ].forEach((property) => this.style.removeProperty(property));
      this.clearStoryItemStyles();
      this.scheduleDiamondHitboxSync();

      if (this.stage) {
        this.stage.setAttribute('aria-hidden', 'false');
        this.stage.removeAttribute('inert');
      }
      if (this.intro) {
        this.intro.setAttribute('aria-hidden', 'true');
        this.intro.setAttribute('inert', '');
      }
      if (this.doorTrigger) this.doorTrigger.hidden = true;
      if (this.trayTrigger) this.trayTrigger.hidden = true;

      if (wasMobileStory) {
        window.requestAnimationFrame(() => {
          if (this.isConnected) this.scrollIntoView({ block: 'start' });
        });
      }

      this.introCleanupTimer = window.setTimeout(() => {
        if (this.intro) this.intro.hidden = true;
        this.unloadVideo();
      }, this.transitionMs + 80);

      if (options.focusFirstDiamond || focusWasInIntro) {
        window.setTimeout(() => {
          const firstDiamond = this.activeBatch() && this.activeBatch().querySelector('[data-vdt-diamond]');
          const visibleNextButton = this.nextButton && !this.nextButton.hidden ? this.nextButton : null;
          const fallbackTarget = visibleNextButton || this.querySelector('[data-vdt-link]') || this.stage;
          const focusTarget = firstDiamond || fallbackTarget;
          if (focusTarget) focusTarget.focus({ preventScroll: true });
        }, this.transitionMs + 100);
      }
    }

    unloadVideo() {
      this.clearWatchdog();
      this.clearStorySeek();
      if (!this.video) return;

      try {
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.querySelectorAll('source').forEach((source) => source.remove());
        this.video.load();
        this.video.remove();
      } catch (_error) {
        // A detached media element can throw in older browsers; it is safe to ignore.
      }

      this.video = null;
      if (this.videoHost) this.videoHost.replaceChildren();
    }

    toggleSound(button) {
      if (!this.video) return;

      const willEnableSound = this.video.muted;
      this.video.muted = !willEnableSound;
      button.setAttribute('aria-pressed', String(willEnableSound));
      button.textContent = willEnableSound
        ? button.dataset.labelOff || '静音'
        : button.dataset.labelOn || '开启声音';

      if (willEnableSound) {
        const shouldResumeStory =
          this.storyMode === 'desktop' && this.classList.contains('is-story-walking');
        if (shouldResumeStory) {
          const playPromise = this.video.play();
          if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
        }
      }
    }

    activateDiamond(diamond) {
      if (this.selectedDiamond === diamond) {
        if (this.designMode) {
          this.announce(`${diamond.dataset.name || '此钻石'} 已选中。主题编辑器中不会打开链接。`);
          return;
        }

        if (!diamond.dataset.link) {
          this.announce(`${diamond.dataset.name || '此钻石'} 尚未配置链接。`);
          return;
        }

        this.navigate(diamond.dataset.link);
        return;
      }

      this.selectDiamond(diamond, true);
    }

    selectDiamond(diamond, announceSelection) {
      this.clearSelection(false);
      this.selectedDiamond = diamond;
      diamond.classList.add('is-selected');
      diamond.setAttribute('aria-pressed', 'true');
      this.classList.add('has-selection');
      this.showZoom(diamond, announceSelection);

      if (this.selectionTip) this.selectionTip.hidden = false;

      if (announceSelection) {
        this.announce(`${diamond.dataset.name || '此钻石'} 已选中。${this.selectionHint}`);
      }

      this.dispatchEvent(
        new CustomEvent('valthea:diamond-select', {
          bubbles: true,
          detail: {
            blockId: diamond.dataset.blockId,
            batch: diamond.dataset.batch,
            slot: diamond.dataset.slot,
            name: diamond.dataset.name
          }
        })
      );
    }

    clearSelection(announceChange, restoreFocus = false) {
      const previouslySelected = this.selectedDiamond;
      this.removeZoom();
      if (this.selectedDiamond) {
        this.selectedDiamond.classList.remove('is-selected');
        this.selectedDiamond.setAttribute('aria-pressed', 'false');
      }

      this.selectedDiamond = null;
      this.classList.remove('has-selection');
      if (this.selectionTip) this.selectionTip.hidden = true;
      if (announceChange) this.announce('已取消选择。');
      if (restoreFocus && previouslySelected && previouslySelected.isConnected) {
        previouslySelected.focus({ preventScroll: true });
      }
    }

    isMobilePortrait() {
      return window.matchMedia(
        `(max-width: ${this.mobileBreakpoint}px) and (orientation: portrait)`
      ).matches;
    }

    parseDiamondCarat(diamond) {
      const explicitValue = String(diamond.dataset.carat || '').replace(/[，,]/g, '.').trim();
      const explicitCarat = Number(explicitValue);
      if (Number.isFinite(explicitCarat) && explicitCarat > 0) return explicitCarat;

      const candidates = [diamond.dataset.caratLabel, diamond.dataset.name];
      for (const candidate of candidates) {
        const normalized = String(candidate || '').replace(/[，,]/g, '.').trim();
        const unitMatch = normalized.match(/([0-9]+(?:\.[0-9]+)?)\s*(?:ct|carats?|克拉)(?![a-z])/i);
        const plainMatch = normalized.match(/^([0-9]+(?:\.[0-9]+)?)$/);
        const parsedCarat = Number((unitMatch || plainMatch || [])[1]);
        if (Number.isFinite(parsedCarat) && parsedCarat > 0) return parsedCarat;
      }

      return null;
    }

    applyZoomPresentation(diamond, view) {
      if (!diamond || !view) return;

      const isMobilePortrait = this.isMobilePortrait();
      const rawRotation = isMobilePortrait
        ? diamond.dataset.zoomRotationMobile
        : diamond.dataset.zoomRotationDesktop;
      const rotation = Number(rawRotation);
      view.style.setProperty('--vdt-zoom-rotation', `${Number.isFinite(rotation) ? rotation : 0}deg`);

      const carat = this.parseDiamondCarat(diamond);
      const rawScale = carat ? Math.cbrt(carat / 3) : 1;
      const minScale = isMobilePortrait ? 0.78 : 0.7;
      const maxScale = isMobilePortrait ? 1.25 : 1.45;
      const caratScale = Math.min(maxScale, Math.max(minScale, rawScale));
      view.style.setProperty('--vdt-zoom-carat-scale', caratScale.toFixed(4));

      if (carat) {
        view.dataset.vdtCarat = String(carat);
      } else {
        delete view.dataset.vdtCarat;
      }
    }

    showZoom(diamond, focusButton) {
      this.removeZoom();

      const view = document.createElement('div');
      view.className = 'vdt__zoom';
      view.dataset.vdtZoomOwner = this.sectionId;
      view.style.setProperty(
        '--vdt-spotlight-color',
        window.getComputedStyle(this).getPropertyValue('--vdt-spotlight-color') || '#f7d7a6'
      );
      const glowScale = Number(window.getComputedStyle(diamond).getPropertyValue('--vdt-glow-scale'));
      view.style.setProperty(
        '--vdt-zoom-glow-scale',
        String(Number.isFinite(glowScale) ? Math.min(2.4, Math.max(1.2, glowScale)) : 1.7)
      );
      this.applyZoomPresentation(diamond, view);

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'vdt__zoom-button';
      button.setAttribute('aria-label', `${diamond.dataset.name || '裸钻'}，${this.selectionHint}`);

      const media = document.createElement('span');
      media.className = 'vdt__zoom-media';
      media.setAttribute('aria-hidden', 'true');

      const gem = document.createElement('span');
      gem.className = 'vdt__zoom-gem';

      if (diamond.dataset.zoomSrc) {
        const image = document.createElement('img');
        image.className = 'vdt__zoom-image';
        image.src = diamond.dataset.zoomSrc;
        image.alt = '';
        image.decoding = 'async';
        image.addEventListener('load', () => {
          if (this.zoomView === view) this.updateZoomMeteor();
        }, {
          signal: this.abortController.signal
        });
        gem.appendChild(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'vdt__zoom-placeholder';
        placeholder.textContent = '◇';
        gem.appendChild(placeholder);
      }

      for (let meteorIndex = 0; meteorIndex < 3; meteorIndex += 1) {
        const meteor = document.createElement('span');
        meteor.className = 'vdt__zoom-meteor';
        meteor.dataset.vdtMeteorIndex = String(meteorIndex);
        gem.appendChild(meteor);
      }

      media.appendChild(gem);

      const caption = document.createElement('span');
      caption.className = 'vdt__zoom-caption';
      caption.textContent = diamond.dataset.link && !this.designMode
        ? this.selectionHint
        : this.zoomReturnHint;

      button.append(media, caption);
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.selectedDiamond) this.activateDiamond(this.selectedDiamond);
      }, { signal: this.abortController.signal });

      view.appendChild(button);
      document.body.appendChild(view);
      this.zoomView = view;
      this.updateZoomMeteor();

      if (focusButton) button.focus({ preventScroll: true });
    }

    updateZoomMeteor() {
      if (!this.zoomView) return;

      const meteors = Array.from(this.zoomView.querySelectorAll('.vdt__zoom-meteor'));
      const visual = this.zoomView.querySelector('.vdt__zoom-image, .vdt__zoom-placeholder');
      if (!meteors.length || !visual || typeof meteors[0].animate !== 'function') return;

      this.zoomMeteorAnimations.forEach((animation) => animation.cancel());
      this.zoomMeteorAnimations = [];
      meteors.forEach((meteor) => meteor.classList.remove('is-active'));

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      if (!visual.offsetWidth || !visual.offsetHeight) return;

      const rotation = (parseFloat(this.zoomView.style.getPropertyValue('--vdt-zoom-rotation')) || 0) * Math.PI / 180;
      const rawCaratScale = parseFloat(this.zoomView.style.getPropertyValue('--vdt-zoom-carat-scale'));
      const caratScale = Number.isFinite(rawCaratScale) ? rawCaratScale : 1;
      const cos = Math.cos(rotation);
      const sin = Math.sin(rotation);

      const isPlaceholder = visual.classList.contains('vdt__zoom-placeholder');
      const radiusX = isPlaceholder
        ? visual.offsetWidth * caratScale / Math.SQRT2 + 10
        : visual.offsetWidth * caratScale / 2 + 10;
      const radiusY = isPlaceholder
        ? radiusX
        : visual.offsetHeight * caratScale / 2 + 10;
      const segmentCount = 64;
      let points = Array.from({ length: segmentCount + 1 }, (_unused, index) => {
        const angle = -Math.PI / 2 + index * Math.PI * 2 / segmentCount;
        return [radiusX * Math.cos(angle), radiusY * Math.sin(angle)];
      });

      points = points.map(([x, y]) => ({
        x: x * cos - y * sin,
        y: x * sin + y * cos
      }));

      const duration = 3600;
      this.zoomMeteorAnimations = meteors.map((meteor, meteorIndex) => {
        const dotHalfSize = meteor.getBoundingClientRect().width / 2;
        const keyframes = points.map(({ x, y }, index) => ({
          offset: index / (points.length - 1),
          transform: `translate(${(x - dotHalfSize).toFixed(2)}px, ${(y - dotHalfSize).toFixed(2)}px)`
        }));
        const animation = meteor.animate(keyframes, {
          duration,
          delay: -meteorIndex * duration / meteors.length,
          iterations: Infinity,
          easing: 'linear'
        });

        // Keep one unchanged orbit and distribute the three meteors evenly on it.
        meteor.classList.add('is-active');
        return animation;
      });
    }

    removeZoom() {
      this.zoomMeteorAnimations.forEach((animation) => animation.cancel());
      this.zoomMeteorAnimations = [];
      if (this.zoomView) this.zoomView.remove();
      this.zoomView = null;
    }

    nextBatch() {
      if (this.batches.length <= 1) return;
      const nextIndex = (this.activeIndex + 1) % this.batches.length;
      this.showBatch(nextIndex, true);

      this.dispatchEvent(
        new CustomEvent('valthea:batch-change', {
          bubbles: true,
          detail: {
            index: this.activeIndex,
            batch: this.activeBatch() ? this.activeBatch().dataset.batch : ''
          }
        })
      );
    }

    showBatch(index, announceChange) {
      if (!this.batches.length) {
        this.activeIndex = 0;
        this.updateBatchControls();
        return;
      }

      const clampedIndex = Math.max(0, Math.min(index, this.batches.length - 1));
      this.clearSelection(false);
      this.activeIndex = clampedIndex;
      this.storyRevealCache = null;

      this.batches.forEach((batch, batchIndex) => {
        const isActive = batchIndex === clampedIndex;
        batch.classList.toggle('is-active', isActive);
        batch.setAttribute('aria-hidden', String(!isActive));
        batch.toggleAttribute('inert', !isActive);
        batch.querySelectorAll('[data-vdt-diamond]').forEach((diamond) => {
          diamond.tabIndex = isActive ? 0 : -1;
        });
      });

      this.updateBatchControls();
      this.scheduleDiamondHitboxSync();
      if (announceChange) this.announce(`第 ${this.activeIndex + 1} / ${this.batches.length} 批。`);
    }

    showBatchByNumber(batchNumber, announceChange) {
      const index = this.batches.findIndex((batch) => batch.dataset.batch === String(batchNumber));
      this.showBatch(index >= 0 ? index : 0, announceChange);
    }

    activeBatch() {
      return this.batches[this.activeIndex] || null;
    }

    updateBatchControls() {
      if (this.nextButton) this.nextButton.hidden = this.batches.length <= 1;
      if (this.pageIndicator) {
        this.pageIndicator.hidden = this.batches.length === 0;
        this.pageIndicator.textContent = this.batches.length
          ? `${this.activeIndex + 1} / ${this.batches.length}`
          : '';
      }
    }

    prepareNavigation(rawUrl) {
      if (!rawUrl) {
        this.announce('此项目尚未配置链接。');
        return null;
      }

      let target;
      try {
        target = new URL(rawUrl, window.location.href);
      } catch (_error) {
        this.announce('此项目的链接无效。');
        return null;
      }

      if (!['http:', 'https:'].includes(target.protocol)) {
        this.announce('此项目的链接协议不受支持。');
        return null;
      }

      if (target.origin === window.location.origin) {
        const batch = this.activeBatch() ? this.activeBatch().dataset.batch : '1';
        const context = {
          v: 1,
          origin: window.location.origin,
          trayPath: window.location.pathname,
          destinationPath: target.pathname,
          batch,
          label: this.returnLabel,
          createdAt: Date.now()
        };

        storageSet(RETURN_KEY, JSON.stringify(context));
        target.searchParams.set('valthea_from', 'tray');
        this.markIntroSeen();
      }

      return target;
    }

    navigate(rawUrl) {
      const target = this.prepareNavigation(rawUrl);
      if (!target) return;

      this.clearSelection(false);
      this.classList.add('is-navigating');
      window.location.assign(target.href);
    }

    consumeReturnContextForThisTray() {
      const context = parseReturnContext();
      if (!context || context.trayPath !== window.location.pathname) return null;
      storageRemove(RETURN_KEY);
      return context;
    }

    introStorageKey() {
      return `valthea:intro:${window.location.pathname}:v${this.introVersion}`;
    }

    markIntroSeen() {
      if (this.designMode) return;
      storageSet(this.introStorageKey(), '1');
    }

    validateEditorConfiguration() {
      if (!this.designMode || !this.editorWarning) return;

      const messages = [];
      if (this.querySelector('.vdt__background-placeholder')) {
        messages.push('尚未上传空托盘背景图。');
      }

      this.batches.forEach((batch) => {
        const usedSlots = new Set();
        const duplicateSlots = new Set();
        batch.querySelectorAll('[data-vdt-diamond]').forEach((diamond) => {
          const slot = diamond.dataset.slot;
          if (usedSlots.has(slot)) duplicateSlots.add(slot);
          usedSlots.add(slot);
        });

        if (duplicateSlots.size) {
          messages.push(`第 ${batch.dataset.batch} 批存在重复位置编号：${Array.from(duplicateSlots).join('、')}。`);
        }
      });

      if (messages.length) {
        this.editorWarning.textContent = messages.join(' ');
        this.editorWarning.hidden = false;
      }
    }

    announce(message) {
      if (!this.status) return;
      this.status.textContent = '';
      window.requestAnimationFrame(() => {
        this.status.textContent = message;
      });
    }

    clearTimers() {
      this.clearWatchdog();
      this.clearIntroPlaybackRecovery();
      this.clearStoryRuntime();
      if (this.introCleanupTimer) window.clearTimeout(this.introCleanupTimer);
      this.introCleanupTimer = null;
    }

    scheduleWatchdog(video = this.video) {
      this.clearWatchdog();
      this.watchdogTimer = window.setTimeout(() => {
        this.watchdogTimer = null;
        if (video && video !== this.video) return;
        if (this.storyFirstFrameReady) return;
        this.revealTray();
      }, this.fallbackSeconds * 1000);
    }

    clearWatchdog() {
      if (this.watchdogTimer) window.clearTimeout(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }

  customElements.define(ELEMENT_NAME, ValtheaDiamondTray);
})();
